import { Vector3 } from 'three'
import { BoxSizeEnum } from '../core/enums'

/**
 * Every number the gameplay depends on, in one place, tagged by how much we
 * actually know about it.
 *
 *   RECOVERED — read straight out of the decompiled code or a prefab. Trust it.
 *   PLACEHOLDER — the original value was a `[SerializeField]` that IL2CPP
 *   dropped and the kit has no bundle to recover it from. Tune against the
 *   reference clip. See docs/PORTING.md §5.
 */

// ---------------------------------------------------------------------------
// Bus geometry — RECOVERED from ConveyorBox (S/M/L).prefab
// ---------------------------------------------------------------------------

/** BoxCollider on the `MovementHandler` child. Agrees with Bus_N_single.obj. */
export const BUS_LENGTH: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 7.97,
  [BoxSizeEnum.Medium]: 10.09,
  [BoxSizeEnum.Large]: 14.08,
}
export const BUS_WIDTH = 4
/**
 * 📐 `_BaseColor` of `Material/IceBus.mat` — (0.7971698, 0.9627745, 1) in
 * **sRGB**, which is 0xCBF6FF.
 *
 * ⚠️ **Which material this is took an elimination, and the ice audit named the
 * wrong one.** `Bus.iceMaterial` is field 0x78, assigned through a
 * `[SerializeField]` that AssetRipper stripped, so the binding is not in the
 * prefab. The export holds exactly two ice materials:
 *
 *  - `Ice 1.mat` — referenced once by **each of the three bus prefabs**, from
 *    the `m_Materials` of the `ice_vfx` ParticleSystemRenderer. Accounted for.
 *  - `IceBus.mat` — referenced by **nothing at all** in the whole export except
 *    its own `.meta`, and its parameter set is a bespoke ice shader (crack,
 *    crystal, bubble, fresnel). An orphan material named for the bus is exactly
 *    what a stripped `[SerializeField] Material` leaves behind.
 *
 * So `iceMaterial` is `IceBus`, and the audit's "`Material/Ice 1.mat`" was the
 * particle's. Frozen in `iceMaterial.test.ts`.
 *
 * ⚠️ **What this constant is for changed with FRZ-8.** An earlier note here
 * said the original *appended* `IceBus` as an extra `sharedMaterials` slot and
 * the port "still draws a box" tinted with this colour. Both halves were wrong:
 * `Bus.ApplyIceMaterialState` (`__0x20f9eac.c`:84 `plVar3[4] = iceMaterial`,
 * element [0] of an IL2CPP array) **replaces slot 0** — the iced body is drawn
 * in `IceBus.mat` itself, opaque (`_Surface 0`), and the port now does the
 * same through the manifest (`palette.ICE_MATERIAL_NAME`, `BusFleet.build`).
 * The box is gone, and with it the opacity that had nothing to be read from.
 *
 * Nothing draws with this number any more: the shard pool of `ice_vfx` used it
 * as a stand-in for `Ice 1.mat` until 2026-09-04, when it moved to that
 * material's own `_Color` (`ICE_SHARD_MATERIAL`). Kept as the read of
 * `IceBus._BaseColor` that `iceMaterial.test.ts` pins, and as the number the
 * manifest's `IceBus` spec is checked against.
 */
export const ICE_BASE_COLOUR = 0xcbf6ff

/**
 * `Material/Ice 1.mat` — what the `ice_vfx` shards are drawn in, as far as the
 * export lets it be read.
 *
 * 🔍 Read off the `.mat`:
 *   - `_Color: (0.4103773, 0.58418816, 1)` — sRGB, like every colour in the
 *     kit (`mat-colors-are-srgb`). The material's own tint, next to
 *     `_Fresnel_Color` (0.412, 0.584, 1) and `_GColor` (0.437, 0.703, 0.849):
 *     three blues, no yellow anywhere in the file.
 *   - `_Surface: 0`, `_SrcBlend: 1`, `_DstBlend: 0`, `_ZWrite: 1`,
 *     `RenderType: Opaque`, queue 2999 — the shard is **opaque**. Nothing in
 *     the blend state can show the alpha ramp `ColorModule` writes.
 *
 * ❓ What the shader does with the particle colour. `RuffGames_IceShader` is a
 * `DummyShaderTextExporter` stub; `_VertexColorPower: 0.92` in the `.mat` and
 * the renderer's `m_VertexStreams` (Color included) say the colour stream *is*
 * fed and *is* looked at, but how it is combined with the three blues is gone
 * with the HLSL. Multiplying it straight in — pale blue × the yellow → orange
 * `ColorModule` gradient — is what the port did until 2026-09-04, and it drew
 * the burst **yellow-green** turning brown, a colour no ice material in the
 * kit carries. The user rejected that on sight (level 25).
 *
 * **PLACEHOLDER, user's call (2026-09-04):** the shards are drawn in the ice
 * **shell's own material**, `IceBus` — the `ToonMaterial` the iced body wears
 * (FRZ-8: pale ice blue, `BusTex_Ice`, ramp 0.244), lit and opaque — so a
 * piece looks like the shell it just came off. `_Color` flat and unlit was
 * tried first and drew a cobalt that read as confetti; the user rejected that
 * too. The gradient is **not** multiplied in (`usesParticleColour: false`),
 * but it is still read and kept in `ICE_BREAK_VFX.colourOverLifetime` — it is
 * prefab data, and the moment a readable shader says how `Ice 1` combines its
 * blues with it, `drawnAs` and this flag change and nothing else moves. §10
 * of `GAMEPLAY-CHECKLIST.md`, row "`ice_vfx` — màu và blend của `Ice 1.mat`".
 */
export const ICE_SHARD_MATERIAL = {
  /** 🔍 `_Color` of `Ice 1.mat`, sRGB — kept as the read; not what is drawn. */
  colour: [0.4103773, 0.58418816, 1] as const,
  /** 🔍 `_Surface 0` / `_ZWrite 1` / `_SrcBlend 1, _DstBlend 0` — the shard is opaque. */
  opaque: true,
  /**
   * ❓ PLACEHOLDER — the manifest material the pool draws the shards with:
   * the iced body's own (`palette.ICE_MATERIAL_NAME`), not `Ice 1`.
   */
  drawnAs: 'IceBus',
  /** ❓ PLACEHOLDER — see above. */
  usesParticleColour: false,
} as const
/**
 * 📐 `ice_vfx` — the burst of shards when the last ice chip goes.
 *
 * `Bus.BreakIce` (`__0x20fb820.c`:91-95) plays it. Everything below is read
 * straight out of `ConveyorBox (M).prefab`, and **none of it was stripped**:
 * `ParticleSystem` and `ParticleSystemRenderer` are native Unity components
 * (`!u!198` / `!u!199`), not MonoBehaviours, so AssetRipper exports them whole.
 * That is the opposite of the ice **count** (IC-3), whose TextMeshPro component
 * is a bare stub — worth stating, because "AssetRipper stripped it" is true of
 * so much here that it is easy to assume of everything.
 *
 * Unity's `minMaxState` says how to read each `MinMaxCurve`: **0** is a single
 * constant (`scalar` only — `minScalar` is whatever the editor last held and is
 * dead), **1** a single curve, **3** RandomBetweenTwoConstants stored as
 * `minScalar` (low) and `scalar` (high) — hence the pairs.
 *
 * ⚠️ `gravityModifier` was read here as the pair `[0.7, 1]` for eight ticks. Its
 * `minMaxState` is **0**: the value is `scalar` = 1, and the 0.7 in `minScalar`
 * is an editor leftover Unity never reads. `iceMaterial.test.ts` froze the wrong
 * reading because its `range()` helper ignored `minMaxState` (FRZ-2).
 *
 * Drawn by `sessionView.fireIceBurst` / `syncIceBurst`, simulated by
 * `render/iceBurst.ts`. Every module that is `enabled: 1` in the prefab has an
 * entry below; the ones that are `enabled: 0` (Rotation, Noise, Force, Limit
 * Velocity, Sub, Trail, …) have none, deliberately.
 */
export const ICE_BREAK_VFX = {
  /** `lengthInSec`. `looping: 0`, `playOnAwake: 0` — one shot, fired by BreakIce. */
  lengthSeconds: 1,
  /** One burst at t = 0; `rateOverTime` is 0, so this is the entire emission. */
  burstCount: 60,
  /** `startLifetime`, RandomBetweenTwoConstants. */
  lifetimeSeconds: [1, 1.5] as const,
  /** `startSpeed`. */
  speed: [1, 2] as const,
  /** `startSize`. `size3D: 0`, so `startSizeY/Z` (both 1) are dead. */
  size: [3, 4.8] as const,
  /**
   * `gravityModifier` — a fraction of `Physics.gravity`, not m/s² itself.
   * `minMaxState: 0` — a **constant**, see the header. Not `[0.7, 1]`.
   */
  gravityModifier: 1,
  /**
   * `startRotation`, RandomBetweenTwoConstants `[0, 6.283185]` radians — one
   * full turn about **Z**, because `rotation3D: 0` makes this the only axis
   * Unity reads. `startRotationX` / `startRotationY` carry `±2π` pairs but are
   * dead for the same reason. `m_RenderAlignment: 1` (World) on the renderer:
   * the axis is the world's Z, not the camera's.
   */
  startRotation: [0, 6.283185] as const,
  /** `maxNumParticles`; the burst is far under it, so it never clips. */
  maxParticles: 1000,
  /**
   * `SizeModule` (`enabled: 1`, `separateAxes: 0`, prefab `:18068`): a single
   * curve over the particle's normalised age, `minMaxState: 1`. Three keys —
   * `(0, 1)`, `(0.9, 1)`, `(1, 0)` — with the slopes Unity stored, so a shard
   * holds its `startSize` for 90 % of its life and then shrinks to nothing.
   * Hermite with these tangents (out −9.999998 at 0.9, in −9.999998 at 1) is
   * exactly the straight line `1 − (t − 0.9) / 0.1`; `iceBurst.test.ts` checks
   * the evaluator against that closed form.
   */
  sizeOverLifetime: [
    { time: 0, value: 1, inSlope: 0, outSlope: 0 },
    { time: 0.9, value: 1, inSlope: -0, outSlope: -9.999998 },
    { time: 1, value: 0, inSlope: -9.999998, outSlope: -9.999998 },
  ] as const,
  /**
   * `ColorModule` (`enabled: 1`, prefab `:18239`), a single gradient in Blend
   * mode (`m_Mode: 0`). Key times are Unity's 16-bit fixed point (`ctime` /
   * `atime` over 65535), kept as written rather than rounded — the last colour
   * key sits at 65267, ≈ 0.996, not at 1.
   *
   * ⚠️ The colour keys are **yellow → orange**, on what the port draws as ice.
   * That is what the prefab says; how it reaches the screen went with the
   * shader (`Ice 1.mat` is `RuffGames_IceShader`, an HLSL stub — FRZ-4), so
   * whether the original multiplied this in at all is ❓ (checklist §10).
   * Since 2026-09-04 the view does **not** multiply it in
   * (`ICE_SHARD_MATERIAL.usesParticleColour`); the keys stay here as prefab
   * data and `iceBurst.ts` still evaluates them per shard.
   */
  colourOverLifetime: {
    colour: [
      { time: 0 / 65535, rgb: [1, 0.95244116, 0.43382353] },
      { time: 65267 / 65535, rgb: [1, 0.347, 0] },
    ],
    alpha: [
      { time: 0 / 65535, alpha: 0 },
      { time: 14649 / 65535, alpha: 1 },
      { time: 54713 / 65535, alpha: 1 },
      { time: 65535 / 65535, alpha: 0 },
    ],
  } as const,
  /**
   * `VelocityModule` (`enabled: 1`, prefab `:18376`). Linear x/y/z and every
   * orbital term are 0; what is on is `radial` 0.5 (`minMaxState: 0`) — half a
   * unit per second straight away from the system's origin, which sits 4.36
   * above the bus root (`ice_vfx` transform `:17638`) — and **`speedModifier`
   * 2** (`minMaxState: 0`), a multiplier on the whole velocity the particle
   * moves by. `inWorldSpace: 0`.
   *
   * The port used to move shards at their bare `startSpeed`, i.e. half the
   * distance per frame the prefab asks for.
   */
  velocityOverLifetime: { radial: 0.5, speedModifier: 2 } as const,
  /**
   * `CollisionModule` (`enabled: 1`, prefab `:19316`): `type: 1` World,
   * `collidesWith` bit 6 = layer **Ground** (`TagManager.asset:20`). The only
   * collider on that layer in `Gameplay.unity` is `Ground`'s `MeshCollider`
   * (&129) over `Ground.asset`, whose AABB is `y ± 3e-16` at a transform of
   * identity rotation and origin — a plane at world y = 0. `GroundTop`
   * (&31) and `SplineMesh (1)` (&13) share the layer but carry no collider, so
   * shards fall through the platform and stop at the ground plane.
   *
   * `m_Dampen` 0.1, `m_Bounce` 0.25, `m_EnergyLossOnCollision` 0,
   * `radiusScale` 0.01, `minKillSpeed` 0, `maxKillSpeed` 10000 — all
   * `minMaxState: 0` constants. Nothing is killed on contact.
   *
   * ❓ How Unity composes dampen and bounce into the post-contact velocity is
   * native code the kit does not carry; `iceBurst.ts` says what it assumes.
   */
  collision: {
    groundY: 0,
    dampen: 0.1,
    bounce: 0.25,
    radiusScale: 0.01,
  } as const,
  /**
   * `ShapeModule.type: 13` is MeshRenderer, `placementMode: 2` is Triangle, and
   * the renderer it samples is `Ice/Fake`'s — which is why that node exists with
   * its own renderer switched off. `randomDirectionAmount: 0`, so a shard leaves
   * along the surface normal of the triangle it was born on.
   */
  shape: { fromMeshRenderer: 'Ice/Fake', placement: 'triangle' } as const,
  /**
   * `m_RenderMode: 4` is Mesh, with four meshes at equal weighting. They are
   * `Stone_1..4` — chips of rubble reused as ice shards.
   *
   * They hang off a ParticleSystemRenderer, which the drawn-subtree walk of
   * `npm run assets` does not follow, so `buildAssets.mjs` asks for them by
   * name (`ICE_SHARD_MESHES`) — all four are in the manifest. This comment
   * said "not in the port's manifest" until FRZ-2; that was true before
   * e44f389 and stale after.
   */
  meshes: ['Stone_1', 'Stone_2', 'Stone_3', 'Stone_4'] as const,
  /** `m_MaxParticleSize` (viewport fraction) and `m_LengthScale`. */
  maxParticleSize: 0.5,
  lengthScale: 0.15,
  /** `m_Materials` — `Ice 1.mat`, the particle's own, **not** `IceBus.mat`. */
  material: 'Ice 1',
} as const

/**
 * 📐 `Bus.iceCountdownParticle` (0x88) — the puff a bus makes when its ice
 * **count drops** but the shell survives (IC-5c).
 *
 * `Bus.DecreaseIceCount` (`__0x20fb6b0.c`:59-81) plays two things and only two:
 * `SoundService.Play(0xc)` — `IceCountdown`, see `soundLibrary.ts` — and then
 * `ParticleSystem.Play_Injected(ptr, **1**)` on this field. That second argument
 * is `withChildren`, and it is what makes the three children go off with the
 * parent. They are **not** sub-emitters: `SubModule.enabled` is `0` on all four
 * systems, so nothing spawns anything else.
 *
 * Distinct from `ICE_BREAK_VFX`, which is `iceBreakParticle` (0x90) and fires
 * once, from `BreakIce`, when the last chip goes.
 *
 * ⚠️ **Recovered, not ported.** Nothing draws this yet. All four systems are
 * textured sprite billboards on additive or alpha-blended materials, and the
 * software rasteriser has no blending — it can alpha-**test** a sprite (IC-12)
 * but not add one. Standing up the visual is IC-5c-ii; this is the spec it will
 * need, frozen while the prefab is in front of us. `web/tests/iceCountVfx.test.ts`
 * holds every number here against the prefab.
 */
export const ICE_COUNT_VFX = {
  /**
   * 📐 Where the whole cluster sits: `IceCount`'s transform under `Ice` under
   * the bus root, identity rotation on all three, `m_LocalPosition
   * (0, 4.4, -0.47)` in Unity local — port z is `+0.47` (PORTING §1). The three
   * children sit at (0, 0, 0) under it, so every system shares this origin.
   * `ConveyorBox (S/M/L).prefab` agree on the position; only the scale differs.
   */
  origin: [0, 4.4, -0.47] as const,
  /**
   * 📐 `IceCount`'s `m_LocalScale`, per prefab: S `1.4066`, M `1.3182`, L
   * `1.442104`. `scalingMode: 0` is **Hierarchy** on all four systems, so this
   * multiplies the emission shape below — a 6.5 × 9.5 box is 8.57 × 12.52 on a
   * Medium.
   *
   * 📐 Hierarchy scaling also multiplies `startSize` and `startSpeed` (Unity's
   * `ParticleSystemScalingMode.Hierarchy`: shape, size and speed all take the
   * transform's scale). The numbers below are the **unscaled** prefab values;
   * `iceCountPuff.ts` applies this to all three at birth (FRZ-7). Gravity is
   * `Physics.gravity × gravityModifier` and is left unscaled — ❓ see §10.
   */
  hierarchyScale: [1.4066, 1.3182, 1.442104] as const,
  /**
   * The parent. `looping: 0`, `playOnAwake: 0` — one shot, and the only one of
   * the four whose `lengthInSec` is not 1: it outlasts its own children.
   */
  parent: {
    node: 'IceCount',
    lengthSeconds: 1.5,
    burstCount: 16,
    lifetimeSeconds: [0.3, 0.6] as const,
    size: [0.4, 0.72] as const,
    speed: [5, 10] as const,
    /**
     * 📐 `ShapeModule.type: 5` is **Box** (prefab `:20283`), `m_Scale
     * (6.5, 1, 9.5)` — the bus's footprint, and a particle is born anywhere in
     * that volume (`boxThickness` 0, `randomPositionAmount` 0) and leaves along
     * the shape's **+Z** with no spread (`randomDirectionAmount` 0,
     * `sphericalDirectionAmount` 0). `angle: 25` is serialized but is the
     * cone's parameter; a box does not read it.
     *
     * This said `Cone, angle 25` until FRZ-3, and `iceCountVfx.test.ts` mapped
     * `cone` to `5` — Unity's `ShapeType` has Cone at **4** and Box at 5, so the
     * test agreed with the wrong word.
     */
    shape: { type: 'box', scale: [6.5, 1, 9.5] } as const,
    material: 'snowflake_AB',
    /**
     * 📐 `gravityModifier` (prefab `:20266`): `minMaxState: 0`, `scalar
     * 0.2`. The `minScalar: 0.4` beside it is a collapsed range Unity ignores
     * (see the MinMaxCurve note on `BUS_PARTICLE_VFX`). A fifth of
     * `Physics.gravity`, so the snowflakes sag rather than fall.
     */
    gravityModifier: 0.2,
    /** 📐 `startRotation` `minMaxState: 0`, `scalar 0` — born upright. */
    startRotation: [0, 0] as const,
    /**
     * 📐 `SizeModule` (`enabled: 1`, prefab `:20439`), `minMaxState: 1`, two
     * keys: `(0.45051193, 1)` slopes ∓0.06910371 and `(1, 0)` slopes
     * −4.395002. `m_PreInfinity: 2` holds the first key before 0.45, so a
     * snowflake keeps its size for 45 % of its life and then shrinks to
     * nothing, fastest at the very end.
     */
    sizeOverLifetime: [
      { time: 0.45051193, value: 1, inSlope: -0.06910371, outSlope: -0.06910371 },
      { time: 1, value: 0, inSlope: -4.395002, outSlope: -4.395002 },
    ] as const,
    /**
     * 📐 `ColorModule` (`enabled: 1`, prefab `:20709`), `minMaxState: 1`
     * (Gradient, `maxGradient`), `m_NumColorKeys: 2`, `m_NumAlphaKeys: 2` —
     * `key2..5` hold stale cyan entries the count excludes. White at 0 to
     * `(0.5647059, 1, 1)` at 65535; alpha 1 at both ends.
     */
    colourOverLifetime: {
      colour: [
        { time: 0 / 65535, rgb: [1, 1, 1] },
        { time: 65535 / 65535, rgb: [0.5647059, 1, 1] },
      ],
      alpha: [
        { time: 0 / 65535, alpha: 1 },
        { time: 65535 / 65535, alpha: 1 },
      ],
    } as const,
    /**
     * 📐 `RotationModule` (`enabled: 1`, prefab `:20547`), `separateAxes: 0`
     * so only `curve` (Z) counts; `minMaxState: 2` RandomBetweenTwoCurves with
     * `scalar 21.903881` — the multiplier on **both** curves, in radians per
     * second (1255°/s; `minScalar 3.1415925` is a collapsed constant no curve
     * mode reads). `maxCurve` `(0, 1)` out −1.7000829 → `(1, 0.010988995)`
     * flat; `minCurve` `(0, −0.9395531)` out 2.4243748 → `(1, 0.010989066)`
     * flat. A snowflake picks its blend once at birth and spins fast either
     * way at first, easing to a near-stop by the end of its life.
     */
    rotationOverLifetime: {
      multiplier: 21.903881,
      min: [
        { time: 0, value: -0.9395531, inSlope: 2.4243748, outSlope: 2.4243748 },
        { time: 1, value: 0.010989066, inSlope: 0, outSlope: 0 },
      ],
      max: [
        { time: 0, value: 1, inSlope: -1.7000829, outSlope: -1.7000829 },
        { time: 1, value: 0.010988995, inSlope: 0, outSlope: 0 },
      ],
    } as const,
    /**
     * 📐 `ClampVelocityModule` (`enabled: 1`, prefab `:21174`) — Unity's "Limit
     * Velocity over Lifetime". `separateAxis: 0` so only `magnitude` counts:
     * `minMaxState: 0`, `scalar 1` (the `x`/`y`/`z` curves beside it are 1 too
     * but unread). `dampen 0.25`, `drag` `scalar 0`, `inWorldSpace 0`. So a
     * snowflake that leaves at 5–10 u/s is pulled back towards **1 u/s** by a
     * quarter of the excess each frame — within a dozen frames it is drifting,
     * not flying. The limit is in the system's own units, so `iceCountPuff.ts`
     * multiplies it by `hierarchyScale` the way it does `startSpeed` (FRZ-9).
     */
    clampVelocity: { limit: 1, dampen: 0.25 } as const,
    /**
     * 📐 `CollisionModule` (`enabled: 1`, prefab `:21732`): `type: 1` World,
     * `collisionMode 0` (3D), `collidesWith` `m_Bits 4294967295` — every layer.
     * `m_Dampen` `scalar 0.2`, `m_Bounce` `scalar 0.2`, `m_EnergyLossOnCollision`
     * 0, `radiusScale 0.01`, `minKillSpeed 0`, `maxKillSpeed 10000` — all
     * `minMaxState: 0`. Nothing is killed on contact. The only one of the four
     * with the module on.
     *
     * What it can hit: `Gameplay.unity` has three non-trigger colliders —
     * `Ground`'s `MeshCollider` (the plane y = 0, as `ICE_BREAK_VFX`),
     * `SplineMesh`'s `MeshCollider` (the road surface) and `Visual`'s
     * `BoxCollider` (3.4 × 3.5 × 2.2, a prop). The bus's own `BoxCollider` is
     * `m_IsTrigger: 1`. The port models the ground plane only: a snowflake is
     * born ≥ 3.68 u above the bus root (origin 4.4 — a transform position, not
     * scaled — minus half the box's 1 × the largest hierarchy scale 1.442) and
     * at `gravityModifier` 0.2 falls at most ½ × 0.2 × 9.81 × 0.6² ≈ 0.35 u in
     * its longest life, so from a bus on the ground **no contact is
     * reachable** — `iceCountPuff.test.ts` holds that bound and runs it.
     * ❓ Whether Unity's particle collision sees trigger colliders, and
     * how it composes dampen and bounce, are native (§10); the port reads them
     * as `iceBurst.ts` does.
     */
    collision: { dampen: 0.2, bounce: 0.2, radiusScale: 0.01 } as const,
    /** `UVModule.enabled: 0` (`:20776`) — `snowflake2` is one picture. */
    sheet: null,
    /**
     * 📐 Renderer `m_Pivot {x: 0.4, y: 0, z: 0}` (`!u!199`, prefab `:22394`;
     * S `:21971`, L `:22786` agree) — the quad is offset by 0.4 of its own
     * width along its own x, so the fast `RotationModule` spin above swings the
     * snowflake around a point off its centre rather than turning it in place.
     * The other three renderers are `{0, 0, 0}`. ❓ Which way +x moves the
     * picture is native (§10); the port shifts the geometry by `+pivot × size`.
     */
    pivot: [0.4, 0, 0] as const,
    /**
     * `CustomDataModule` `enabled: 1` (`:22055`) — but `mode0: 0` and
     * `mode1: 0` are `ParticleSystemCustomDataMode.Disabled` for both streams,
     * every `vector0_*`/`vector1_*` is `scalar 0`, and the renderer has
     * `m_UseCustomVertexStreams: 0` with `m_VertexStreams: 0001030405`
     * (Position, Normal, Color, UV, UV2 — no Custom1/2). Nothing reaches the
     * shader, so there is nothing to port; the ❓ "does `snowflake_AB` read it"
     * is moot. `iceCountVfx.test.ts` holds both modes at 0.
     */
  },
  /**
   * The three that ride along on `withChildren`. Each is its own
   * `ParticleSystem` with its own material; the order here is the order they sit
   * under `IceCount` in the prefab.
   */
  children: [
    {
      node: 'Glow',
      lengthSeconds: 1,
      burstCount: 1,
      lifetimeSeconds: [0.3, 0.3] as const,
      size: [10, 10] as const,
      speed: [0, 0] as const,
      /**
       * 📐 `ShapeModule.enabled: 0` (prefab `:22697`) — not a sphere, no shape
       * at all: the one particle is born at the system's origin. (`type: 0`
       * is what a disabled module keeps; this said "Sphere" until FRZ-3.)
       */
      shape: { type: 'disabled' } as const,
      material: 'glow2_ADD',
      /** 📐 `gravityModifier` `minMaxState: 0`, `scalar 0` (`minScalar 2` is dead). */
      gravityModifier: 0,
      /** 📐 `startRotation` `minMaxState: 3`, `[0, 6.283185]` — any angle about Z. */
      startRotation: [0, 6.283185] as const,
      /**
       * 📐 `SizeModule` (`enabled: 1`, prefab `:22853`): `(0, 1)` flat →
       * `(1, 0.2796173)` slopes −1.4372076. The glow shrinks to a bit over a
       * quarter over its 0.3 s.
       */
      sizeOverLifetime: [
        { time: 0, value: 1, inSlope: 0, outSlope: 0 },
        { time: 1, value: 0.2796173, inSlope: -1.4372076, outSlope: -1.4372076 },
      ] as const,
      /**
       * 📐 `ColorModule` (`enabled: 1`, prefab `:23051`): pale ice
       * `(0.7941176, 0.97444224, 1)` at 0 to `(0, 0.6275863, 1)` at 65535;
       * alpha 1 → 0 across the same span. Additive, so the fade is a dimming.
       */
      colourOverLifetime: {
        colour: [
          { time: 0 / 65535, rgb: [0.7941176, 0.97444224, 1] },
          { time: 65535 / 65535, rgb: [0, 0.6275863, 1] },
        ],
        alpha: [
          { time: 0 / 65535, alpha: 1 },
          { time: 65535 / 65535, alpha: 0 },
        ],
      } as const,
      /** `RotationModule.enabled: 0` — the one it was born with is the one it keeps. */
      rotationOverLifetime: null,
      /**
       * 📐 `ClampVelocityModule` (`enabled: 1`, `:23516`): `magnitude` `scalar 1`,
       * `dampen 0.1`, `drag 0`, `separateAxis 0`. Moot at `startSpeed` 0 — the
       * glow never exceeds the limit — but the module is on, so it is here.
       */
      clampVelocity: { limit: 1, dampen: 0.1 } as const,
      /** `CollisionModule.enabled: 0` (`:24074`). */
      collision: null,
      /** `UVModule.enabled: 0` (`:23118`). */
      sheet: null,
      /** Renderer `m_Pivot {0, 0, 0}` (`:24741`). */
      pivot: [0, 0, 0] as const,
    },
    {
      node: 'Smoke',
      lengthSeconds: 1,
      burstCount: 6,
      lifetimeSeconds: [0.5, 0.8] as const,
      size: [2, 3.2] as const,
      speed: [1.5, 3] as const,
      /** 📐 Box, `m_Scale (6.5, 2, 9.5)` (prefab `:25044`) — the tall one. */
      shape: { type: 'box', scale: [6.5, 2, 9.5] } as const,
      material: 'cloud_2x2_soft_AB',
      /** 📐 `gravityModifier` `minMaxState: 0`, `scalar 0` (`minScalar 0.3` is dead). */
      gravityModifier: 0,
      /** 📐 `startRotation` `minMaxState: 3`, `[0, 6.283185]`. */
      startRotation: [0, 6.283185] as const,
      /** `SizeModule.enabled: 0` — a cloud stays the size it was born. */
      sizeOverLifetime: null,
      /**
       * 📐 `ColorModule` (`enabled: 1`, prefab `:25470`): white throughout,
       * alpha 1 at 0 → 0 at 65535. A plain fade-out.
       */
      colourOverLifetime: {
        colour: [
          { time: 0 / 65535, rgb: [1, 1, 1] },
          { time: 65535 / 65535, rgb: [1, 1, 1] },
        ],
        alpha: [
          { time: 0 / 65535, alpha: 1 },
          { time: 65535 / 65535, alpha: 0 },
        ],
      } as const,
      /**
       * 📐 `RotationModule` (`enabled: 1`, prefab `:25308`): the same pair of
       * curves as `IceCount` under a multiplier of `2.5307274` rad/s (145°/s;
       * `minScalar 0.7853982` dead). A slow roll that dies away.
       */
      rotationOverLifetime: {
        multiplier: 2.5307274,
        min: [
          { time: 0, value: -0.9395531, inSlope: 2.4243748, outSlope: 2.4243748 },
          { time: 1, value: 0.010989066, inSlope: 0, outSlope: 0 },
        ],
        max: [
          { time: 0, value: 1, inSlope: -1.7000829, outSlope: -1.7000829 },
          { time: 1, value: 0.010988995, inSlope: 0, outSlope: 0 },
        ],
      } as const,
      /**
       * 📐 `ClampVelocityModule` (`enabled: 1`, `:25917`): `magnitude` `scalar
       * 0.3`, `dampen 0.2`, `drag 0`, `separateAxis 0`. A cloud leaves at
       * 1.5–3 u/s and is held to 0.3 — it barely leaves the roof.
       */
      clampVelocity: { limit: 0.3, dampen: 0.2 } as const,
      /** `CollisionModule.enabled: 0` (`:26475`). */
      collision: null,
      /**
       * 📐 `UVModule` (`enabled: 1`, `:25537`): `tilesX 2`, `tilesY 2`,
       * `animationType 0` WholeSheet, `startFrame 0`, `cycles 1`,
       * `frameOverTime` `minMaxState: 3` RandomBetweenTwoConstants
       * `minScalar 0` → `scalar 0.9999` — one cell of `cloud_2x2_soft`, chosen
       * at birth and held. The 0.9999 is Unity's way of making the last cell
       * reachable without wrapping. `busParticles.frameOf` reads the same shape
       * on the bus's own exhaust; the puff picks its cell the same way. (`Snow`
       * also serializes `tilesX/Y 2` but its module is **off** — grep for
       * `tilesX: 2` finds both.)
       */
      sheet: { tilesX: 2, tilesY: 2 } as const,
      /** Renderer `m_Pivot {0, 0, 0}` (`:27142`). */
      pivot: [0, 0, 0] as const,
    },
    {
      node: 'Snow',
      lengthSeconds: 1,
      burstCount: 35,
      lifetimeSeconds: [0.3, 0.8] as const,
      size: [0.2, 0.6] as const,
      speed: [1, 2] as const,
      /** 📐 Box, `m_Scale (6.5, 1, 9.5)` (prefab `:27445`), same as the parent. */
      shape: { type: 'box', scale: [6.5, 1, 9.5] } as const,
      material: 'snow_AB',
      /**
       * 📐 `gravityModifier` `minMaxState: 0`, `scalar 1` (`minScalar 0.3`
       * dead) — full `Physics.gravity`; the only one of the four that falls.
       */
      gravityModifier: 1,
      /** 📐 `startRotation` `minMaxState: 3`, `[0, 6.283185]`. */
      startRotation: [0, 6.283185] as const,
      /**
       * 📐 `SizeModule` (`enabled: 1`, prefab `:27601`): `(0, 1)` flat →
       * `(1, 0)` slopes −2. Shrinks to nothing, slowly at first.
       */
      sizeOverLifetime: [
        { time: 0, value: 1, inSlope: 0, outSlope: 0 },
        { time: 1, value: 0, inSlope: -2, outSlope: -2 },
      ] as const,
      /**
       * 📐 `ColorModule` (`enabled: 1`, prefab `:27871`): white throughout;
       * alpha 1 until `atime0 42959` (≈ 0.656), then to `0.53333336` at 65535.
       * Note the first alpha key is **not** at 0 — Blend mode holds it flat
       * before that, so the flake is solid for two thirds of its life.
       */
      colourOverLifetime: {
        colour: [
          { time: 0 / 65535, rgb: [1, 1, 1] },
          { time: 65535 / 65535, rgb: [1, 1, 1] },
        ],
        alpha: [
          { time: 42959 / 65535, alpha: 1 },
          { time: 65535 / 65535, alpha: 0.53333336 },
        ],
      } as const,
      /** `RotationModule.enabled: 0`. */
      rotationOverLifetime: null,
      /**
       * 📐 `ClampVelocityModule` (`enabled: 1`, `:28318`): `magnitude` `scalar
       * 0.5`, `dampen 0.1`, `drag 0`, `separateAxis 0`. Held to 0.5 u/s while
       * gravity keeps adding to it — a flake falls faster than it flies.
       */
      clampVelocity: { limit: 0.5, dampen: 0.1 } as const,
      /** `CollisionModule.enabled: 0` (`:28876`). */
      collision: null,
      /** `UVModule.enabled: 0` (`:27938`) — `tilesX/Y 2` are serialized but unread. */
      sheet: null,
      /** Renderer `m_Pivot {0, 0, 0}` (`:29543`). */
      pivot: [0, 0, 0] as const,
    },
  ] as const,
} as const

/**
 * 📐 The bus's own four particle systems — exhaust, trail, depart glow and wind
 * lines — read straight out of `ConveyorBox (M).prefab`.
 *
 * Which decomp field points at which node is the one soft spot, and it is soft
 * for a structural reason: AssetRipper emitted `Assets/Scripts/Assembly-CSharp/
 * Bus.cs` as a **stub with no fields**, so every `MonoBehaviour` block in the
 * prefab ends at `m_EditorClassIdentifier:` with the serialized payload
 * stripped. The bindings below are therefore inferred from name and material,
 * and each carries its own confidence. The *numbers* are not inferred — they
 * are read from the `!u!198` documents and locked by
 * `web/tests/busParticleVfx.test.ts`.
 *
 *     smokeParticle       0x100  →  Parts/Tail/SmokeWhite        (high)
 *     smokeTrailParticle  0x108  →  Parts/Tail/SmokeWhiteTrail   (high)
 *     windLineEffect      0x0F0  →  Parts/Head/WindlinesSpeedy   (high)
 *     trailGlowEffect     0x0F8  →  Parts/Head/IceFloorTrail     (❓ see below)
 *
 * ⚠️ **Two traps in reading a Unity `MinMaxCurve` out of YAML**, both of which
 * this file got wrong on the first pass:
 *
 *  - **`minMaxState: 0` means CONSTANT, and the constant is `scalar`.** The
 *    `minScalar` sitting next to it is whatever the field held before the
 *    designer collapsed the range, and Unity ignores it. `SmokeWhite`'s
 *    `rateOverTime` reads `scalar: 5, minScalar: 10`, which is **5 per second**,
 *    not "5 to 10". Only `minMaxState: 3` is a real two-constant range, and
 *    there the pair is `[minScalar, scalar]` — min second.
 *  - Same for the burst counts in `m_Bursts`: `countCurve` is a `MinMaxCurve`
 *    too, so `scalar: 8, minScalar: 12` is a burst of **8**.
 *
 * `gravityModifier` is a multiplier on `Physics.gravity`, not an acceleration —
 * see `UNITY_GRAVITY` in `render/iceBurst.ts`. Note the **sign**: exhaust smoke
 * is −0.3, so it drifts *up*.
 */
export const BUS_PARTICLE_VFX = {
  /**
   * `smokeParticle` (0x100) — the exhaust puff, `Play()`ed by
   * `Bus.BoxToConveyorMovementStart` (`__0x20fad34.c`) the moment the bus pulls
   * out of its slot, and `Stop()`ed by `BusSplineFollower.TransitionToMainRoad`
   * (`__0x2112084.c`) when it reaches the loop.
   *
   * `moveWithTransform: 0` — world space, so the puff is left behind rather
   * than dragged along.
   */
  smoke: {
    node: 'SmokeWhite',
    lengthSeconds: 1,
    looping: true,
    simulationSpeed: 1,
    /** `moveWithTransform: 0`. */
    worldSpace: true,
    /** `rateOverTime`, `minMaxState: 0` — a flat 5 per second. */
    rateOverTime: 5,
    rateOverDistance: 0,
    lifetimeSeconds: [0.5, 1.5] as const,
    speed: [3, 4] as const,
    size: [1, 1.3] as const,
    /** `startColor`, `minMaxState: 2` — random between two colours. */
    colour: [0.8235294, 1] as const,
    /** Negative: the exhaust rises. */
    gravityModifier: -0.3,
    /**
     * `ClampVelocityModule` — `magnitude` with `separateAxis: 0`, and `dampen`.
     *
     * 🔴 The first pass listed this module as "not modelled", and it is the one
     * that decides what the exhaust looks like. A puff leaves at 3-4 u/s and is
     * pulled back towards **0.7** by 9 % of the excess every frame, so within
     * about a third of a second it stops going anywhere: it hangs at the
     * tailpipe and drifts up. Unmodelled, the same puff shoots several units
     * out before it fades.
     */
    speedLimit: 0.7,
    dampen: 0.09,
    /** `ShapeModule.type: 4` is Cone. */
    shape: { type: 'cone', angle: 15, radius: 0.01 } as const,
    /**
     * `UVModule`: `tilesX: 2, tilesY: 2`, so `cloud_2x2_hardshadow` is a sheet of
     * **four** clouds and a particle shows one of them. `frameOverTime` is a
     * RandomBetweenTwoConstants over 0..0.9999, i.e. a frame picked once at
     * birth and held — not an animation.
     *
     * 🔴 Drawing the whole sheet on one quad, which is what a port that misses
     * this module does, puts four quarter-size clouds in a 2x2 grid on every
     * particle. On screen that reads as clusters of grey specks trailing each
     * bus, and it is easy to misdiagnose as the texture being wrong or the
     * alpha being broken.
     */
    sheet: { tilesX: 2, tilesY: 2 } as const,
    /** `startRotation`, radians — each puff is spun at random about the view. */
    startRotation: [0, 6.283185] as const,
    /** `SizeModule` keys — swells almost at once, then shrinks away. */
    sizeCurve: [
      [0, 0],
      [0.27409843, 0.9749636],
      [1, 0],
    ] as const,
    /** `ColorModule` alpha keys, `atime / 65535`. Fades in and back out. */
    alphaCurve: [
      [0, 0],
      [0.10495155260547799, 1],
      [0.6822156099794003, 1],
      [1, 0],
    ] as const,
    material: 'cloud_2x2_default_AB',
  },

  /**
   * `smokeTrailParticle` (0x108) — the thin trail a bus lays down once it is on
   * the loop. `TransitionToMainRoad` swaps this in as the puff goes out, and
   * `Bus.SetTrailsActive` / `ClearTrails` gate it while the bus is hidden.
   *
   * `rateOverDistance` rather than `rateOverTime`: it emits per world unit
   * travelled, so a stopped bus lays down nothing.
   */
  smokeTrail: {
    node: 'SmokeWhiteTrail',
    lengthSeconds: 1,
    looping: true,
    simulationSpeed: 1,
    /** `moveWithTransform: 1`, `emitterVelocityMode: 1`. */
    worldSpace: false,
    rateOverTime: 0,
    /** Per world unit travelled. */
    rateOverDistance: 3,
    lifetimeSeconds: [0.1, 0.25] as const,
    speed: [0.05000001, 0.15] as const,
    size: [0.7, 0.9] as const,
    colour: [0.8235294, 1] as const,
    gravityModifier: 0.05,
    /** The same clamp as the puff. */
    speedLimit: 0.7,
    dampen: 0.09,
    /** `ShapeModule.type: 0` is Sphere. */
    shape: { type: 'sphere', radius: 0.1 } as const,
    /** The same 2x2 sheet as the puff — see `smoke.sheet`. */
    sheet: { tilesX: 2, tilesY: 2 } as const,
    startRotation: [0, 6.283185] as const,
    sizeCurve: [
      [0.19762844, 1],
      [1, 0],
    ] as const,
    alphaCurve: [
      [0, 1],
      [0.9000076295109484, 1],
      [1, 0],
    ] as const,
    material: 'cloud_2x2_default_AB',
  },

  /**
   * `trailGlowEffect` (0xF8) — the one `PlayDepartEffects` **tints**:
   *
   *     main.startColor = _color.GetEditorColor();   // alpha forced to 1
   *     trailGlowEffect.Play();
   *
   * so this is the coloured streak a full bus leaves as it drives off. The
   * prefab ships it constant white precisely because the colour is written at
   * run time.
   *
   * ❓ **The binding is the weakest claim in this file.** `IceFloorTrail` is
   * chosen because it is the only ParticleSystem under `Parts` left unclaimed
   * once the other three are assigned, it is on the additive `glow1_ADD`
   * material, and its `startColor` is a plain constant white with an alpha
   * curve — the shape of something meant to be recoloured. Its **name** argues
   * the other way: it reads like a leftover from the ice feature. The rival
   * candidate is `Ice/ice_vfx/Glow`, also `glow1_ADD`. Nothing in the export can
   * settle it, because the field bindings were stripped. To close it: find a
   * frame in `reference/gameplay.mp4` where a full bus drives off and see
   * whether the coloured streak comes off the **head** (this node's position) or
   * the roof (`Glow`'s).
   *
   * ⚠️ Its peak alpha is **0.098** — this is a very faint wash, not a flare. A
   * port that draws it at full opacity will look nothing like the original.
   */
  departGlow: {
    node: 'IceFloorTrail',
    lengthSeconds: 0.5,
    looping: true,
    simulationSpeed: 1,
    worldSpace: false,
    rateOverTime: 0,
    rateOverDistance: 3.5,
    lifetimeSeconds: [0.5, 0.5] as const,
    speed: [0, 0] as const,
    size: [10, 10] as const,
    colour: [1, 1] as const,
    gravityModifier: 0,
    /** On, but `startSpeed` is 0, so it never has any speed to clamp. */
    speedLimit: 0.7,
    dampen: 0.09,
    /** Cone with `angle: 0` — a straight line along the emitter's axis. */
    shape: { type: 'cone', angle: 0, radius: 0.01 } as const,
    /** `UVModule` is off here: `glow1` is one picture, not a sheet. */
    sheet: { tilesX: 1, tilesY: 1 } as const,
    startRotation: [0, 6.283185] as const,
    sizeCurve: [
      [0, 1],
      [1, 1],
    ] as const,
    alphaCurve: [
      [0, 0],
      [0.2478065156023499, 0.09803922],
      [1, 0],
    ] as const,
    material: 'glow1_ADD',
  },

  /**
   * `windLineEffect` (0xF0) — the speed streaks, the second thing
   * `PlayDepartEffects` starts.
   *
   * ⚠️ **`m_RenderMode: 5` is None**: the ParticleSystemRenderer draws
   * *nothing*. What you see is the `TrailModule`, which is the only one of the
   * bus's four systems with trails switched on — that is why `m_Materials`
   * carries the builtin `Default-Particle` first and the real material
   * `circle_AB` second, the second slot being the trail material. So a wind line
   * is a **ribbon behind an invisible particle**, not a sprite.
   *
   * `simulationSpeed: 2` — it runs at double rate against everything else.
   */
  windLines: {
    node: 'WindlinesSpeedy',
    lengthSeconds: 5,
    looping: true,
    /** Twice normal; the only system on the bus that is not 1. */
    simulationSpeed: 2,
    worldSpace: true,
    rateOverTime: 0,
    rateOverDistance: 2,
    lifetimeSeconds: [1, 1.2] as const,
    speed: [0, 0] as const,
    size: [0.2, 0.2] as const,
    colour: [1, 1] as const,
    gravityModifier: 0,
    /** The only bus system with `ClampVelocityModule` **off**. */
    speedLimit: null,
    dampen: 0,
    /** A fat sphere: `radius: 3`, so the streaks are thrown around the bus. */
    shape: { type: 'sphere', radius: 3 } as const,
    sheet: { tilesX: 1, tilesY: 1 } as const,
    /** The only bus system with `RotationModule` off and no start spin. */
    startRotation: [0, 0] as const,
    sizeCurve: [
      [0, 0],
      [1, 1],
    ] as const,
    alphaCurve: [
      [0, 1],
      [1, 1],
    ] as const,
    /** `m_RenderMode: 5` (None) — the trail draws, the particle does not. */
    renderMode: 'none-with-trail',
    material: 'circle_AB',
  },
} as const

/**
 * 🔍 `PolygonParticleFX_LightRay_Cube.mat` — the headlight shafts.
 *
 * `CarLights` is switched on by `Bus.BoxToConveyorMovementStart`
 * (`__0x20fad34.c`, `SetActive(*(Bus + 0x110), 1)`); see `Bus.lightsOn`. What it
 * switches on is **not a `Light` component** — a scan of the prefab for class
 * `!u!108` returns nothing. It is `LightL`/`LightR` → `SM_LightRay_Round`, a
 * SkinnedMeshRenderer cone on a three-bone rig.
 *
 *     m_Shader: {fileID: 200, guid: 0000000000000000f000000000000000}
 *     _TintColor: {r: 0.6617647, g: 0.554379, b: 0.2724913, a: 0.13333334}
 *     _MainTex: {fileID: 0}
 *
 * Builtin shader 200 is Unity's legacy **Particles/Additive**, and `_MainTex` is
 * null, so the shaft is an untextured additive amber at 13 % — a soft glow, not
 * a solid. Drawing it with the ordinary toon material instead is what put a
 * white box on the nose of every bus, and is why `CarLights` sat in `SKIP` with
 * the note "the light-ray mesh renders wrong".
 *
 * ❓ **The ×2.** Unity's legacy particle shaders compute
 * `2.0 * i.color * _TintColor * tex2D(...)`, so the drawn colour is twice the
 * authored tint. The shader itself is builtin and is **not** in the export —
 * AssetRipper writes a `DummyShaderTextExporter` stub — so this is taken from
 * how that shader is known to work, not read from this kit. If the glow ever
 * looks half as bright as the clip, this is the number to doubt first.
 */
export const BUS_LIGHT_RAY = {
  /** `_TintColor` rgb, sRGB like every colour read out of a `.mat`. */
  tint: [0.6617647, 0.554379, 0.2724913] as const,
  /** `_TintColor.a`. */
  alpha: 0.13333334,
  /** ❓ The legacy particle shader's constant. See above. */
  gain: 2,
  material: 'PolygonParticleFX_LightRay_Cube',
  mesh: 'SM_LightRay_Round',
  /**
   * 📐 The shape the three-bone rig makes out of the bind-pose mesh.
   *
   * 🔴 **The mesh is a CYLINDER, not a cone.** `SM_LightRay_Round.obj` is a
   * ring of radius 1.0 at y = −1.829 and a capped disc of radius 1.0 at y = 0 —
   * measured, 161 vertices, no taper anywhere. Drawing it as it comes gives a
   * fat tube on the nose of the bus, which is exactly what the first pass did.
   *
   * The cone is entirely the rig's doing, and it is the **bone scales** that
   * make it, not the bone positions:
   *
   *     Light_Shaft_Base    localScale 0.2   localPos (0, 0, 0)
   *     Light_Shaft_Manip   localScale 3.7   localPos (−16.65, 0, 0)   ← child
   *     Null                localScale 1     localPos (0, 0, 0)
   *
   * identical on all three prefabs, with `Light_Shaft_Base` as the root bone.
   * So the lamp end is skinned to a bone at world scale **0.2** and the far end
   * to one at **0.2 × 3.7 = 0.74** — a cone widening from radius 0.2 to 0.74 —
   * and the far bone sits |(−16.65, 0, 0)| × 0.2 = **3.33** units out.
   *
   * ⚠️ The port has no skinning, so the taper is baked into the vertices: a
   * vertex at `t` along the shaft (0 at the lamp, 1 at the tip) has its radius
   * scaled by `lerp(nearRadius, farRadius, t)` and its length mapped onto
   * `length`. That reproduces two-bone linear blend skinning exactly here,
   * because both bones are axis-aligned, unrotated and differ only in scale —
   * which is not true in general and is why this is 📐 rather than 🔍.
   */
  /** The bind-pose mesh's own length along −Y, measured off the OBJ. */
  meshLength: 1.829,
  /** `|Light_Shaft_Manip.localPosition| * Light_Shaft_Base.localScale`. */
  length: 3.33,
  /** `Light_Shaft_Base.localScale` — the lamp end. */
  nearRadius: 0.2,
  /** `Light_Shaft_Base.localScale * Light_Shaft_Manip.localScale`. */
  farRadius: 0.2 * 3.7,
} as const

/**
 * ❗ **TUNING — a deliberate departure from the original, not a reading of it.**
 *
 * Everything else in this file is the prefab's own numbers. This is not: it is
 * a set of multipliers laid over them because the shipped values, faithfully
 * ported, come out too faint to read on this port's pale background.
 *
 * **What the original actually says**, measured, so the deviation stays visible:
 *
 *   - the exhaust puff is **5 particles a second**, grey 0.82–1.0, about 1.2
 *     units across at its widest. A drive out of the parking slot lasts about a
 *     second, so the whole thing is **two or three puffs**;
 *   - the road trail is 3 per unit travelled, 0.7–0.9 across, alive for 0.1–0.25 s;
 *   - the depart glow peaks at alpha **0.098** — a wash, not a flare;
 *   - the wind lines are 0.2 units across, drawn as ribbons the port draws as
 *     sprites (see `BUS_PARTICLE_VFX.windLines`).
 *
 * ⚠️ Read the two together before changing either. `BUS_PARTICLE_VFX` is
 * evidence and is locked against the prefab by `busParticleVfx.test.ts`;
 * **this** block is taste, and nothing outside it may be adjusted for looks
 * (CLAUDE.md §1). If the port ever needs to answer "what did the original do",
 * the answer is the other block, and setting every multiplier here to 1 gets
 * back to it exactly.
 *
 * Authorised by the user on 2026-08-28 — "cứ nâng số giúp tôi vì mấy vấn đề này
 * thuộc về tuning rồi": a decision about presentation, taken knowingly.
 */
export interface BusFxBoost {
  /** Multiplies `rateOverTime` and `rateOverDistance` together. */
  readonly rate: number
  /** Multiplies `startSize`. */
  readonly size: number
  /** Multiplies the alpha curve, clamped at 1. */
  readonly alpha: number
}

export const BUS_FX_BOOST: Readonly<
  Record<'smoke' | 'smokeTrail' | 'departGlow' | 'windLines', BusFxBoost>
> = {
  /** 5/s → 30/s, and half again as wide: the puff becomes a plume. */
  smoke: { rate: 5, size: 1.4, alpha: 1 },
  /** 3/unit → 9/unit, so the road trail is continuous rather than dotted. */
  smokeTrail: { rate: 4, size: 1.5, alpha: 1 },
  /** 0.098 → 0.34 peak. Still a wash, but one that reads at a glance. */
  departGlow: { rate: 1.5, size: 1, alpha: 3.5 },
  /** 0.2 units is a speck once it is a sprite rather than a ribbon. */
  windLines: { rate: 1.5, size: 2.5, alpha: 1 },
}

/**
 * ❗ TUNING, as above — the crash burst.
 *
 * The original is 8 shards, 1 glow and 16 sparks, and with
 * `ClampVelocityModule` damping them to a standstill the whole thing is over
 * inside 0.6 s and covers about a unit. Faithful, and easy to miss entirely.
 */
export const BUS_CRASH_BOOST: BusFxBoost = { rate: 2.5, size: 1.6, alpha: 1 }

/**
 * 📐 Where each emitter sits on the body, per prefab size, in Unity local space.
 *
 * Composed down the transform chain of `ConveyorBox (S|M|L).prefab` — position,
 * rotation and scale at every link, not just the leaf's own `m_LocalPosition`,
 * since `Parts/Tail` and `Parts/Head` both carry offsets of their own.
 *
 * Three things here are worth not smoothing over, because each looks like a
 * mistake and is not:
 *
 *  - **The exhaust is off-centre**: `x: 0.87`, i.e. a tailpipe on one side, not
 *    a plume out of the middle. Both tail systems share the exact same point.
 *  - **`IceFloorTrail` is not on the floor.** It sits at `y: 3.36`, roof height,
 *    and its `z` moves from `+3.383` on Small and Medium to `-0.13` on Large —
 *    it is near the front on the short buses and near the middle on the long
 *    one. Another argument that the name is a leftover (see `departGlow`).
 *  - **`WindlinesSpeedy` is rotated so its cone points straight up.** The
 *    quaternion `(-0.5, 0.5, 0.5, 0.5)` takes local +Z to +Y. With a Sphere
 *    shape and `startSpeed: 0` this changes nothing visible, but a port that
 *    "tidied" the rotation away and later gave the system a speed would be
 *    quietly wrong.
 *
 * ❓ **The Large bus's `SmokeWhite` carries a rotation the other two do not** —
 * `(-0.995, 0, 0, 0.098)`, about 169° about X, which turns its 15° cone from
 * pointing backwards to pointing down and forward. Nothing in the decomp reads
 * or corrects it, so it is most likely an authoring slip that shipped. Recorded
 * as measured rather than normalised to identity: if it turns out to matter,
 * the number is here; if it is a slip, it is the original's slip.
 */
/**
 * Which cap of the body each emitter hangs off, and how far it sits from that
 * cap's own point along the bus.
 *
 * 🔴 **A bus on the road is BENT, and the emitters have to ride the bend.**
 * `BUS_FX_ANCHORS` is a rigid offset from the bus root, which is right for a
 * parked bus and wrong for one on a curve: the root pose is a straight-line
 * approximation of a body that is following the road, so a fixed −4.61 in Z
 * lands somewhere off to the inside of the turn — on the bodywork rather than
 * behind the tail. That is exactly what "the smoke shows up on the side of the
 * bus when it turns" looks like.
 *
 * 🔍 The original has no such problem and the reason is worth stating: the two
 * smoke systems are children of **`Parts/Tail`** and the glow and wind lines of
 * **`Parts/Head`**, and `ApplyPositions` / `UpdateCapBodies` place those two
 * caps **on the curve** as rigid pieces. The emitters are carried by the caps,
 * so they are always exactly at the nose and the tail however the middle bends.
 *
 * So the port asks the follower for the cap's pose — `bodyPose(0)` for the head,
 * `bodyPose(grownBodyLength)` for the tail — and offsets from there. The numbers
 * below are how far each emitter sits **behind its own cap point**, measured off
 * the prefab: `HeadEnd` is at z +5.219 and `TailEnd` at −4.84 on a Medium, so
 * e.g. the exhaust at −4.61 is 0.23 **ahead** of the tail point.
 */
export const BUS_FX_CAPS: Readonly<
  Record<'smoke' | 'smokeTrail' | 'departGlow' | 'windLines', 'head' | 'tail'>
> = {
  smoke: 'tail',
  smokeTrail: 'tail',
  departGlow: 'head',
  windLines: 'head',
}

/**
 * The end points each cap is measured from, per size, in Unity local Z.
 *
 * `HeadEnd` and `TailEnd` are real nodes on the prefab; `bodyPose(0)` lands on
 * the first and `bodyPose(grownBodyLength)` on the second.
 */
export const BUS_CAP_ENDS: Readonly<Record<BoxSizeEnum, { head: number; tail: number }>> = {
  [BoxSizeEnum.Small]: { head: 4.19, tail: -3.89 },
  [BoxSizeEnum.Medium]: { head: 5.219, tail: -4.84 },
  [BoxSizeEnum.Large]: { head: 7.24, tail: -6.83 },
}

export const BUS_FX_ANCHORS: Readonly<
  Record<
    BoxSizeEnum,
    Readonly<Record<'smoke' | 'smokeTrail' | 'departGlow' | 'windLines', readonly [number, number, number]>>
  >
> = {
  [BoxSizeEnum.Small]: {
    smoke: [0.87, 0.43, -3.53],
    smokeTrail: [0.87, 0.43, -3.53],
    departGlow: [0, 3.36, 3.383],
    windLines: [0, 2.4, -1.057],
  },
  [BoxSizeEnum.Medium]: {
    smoke: [0.87, 0.43, -4.61],
    smokeTrail: [0.87, 0.43, -4.61],
    departGlow: [0, 3.36, 3.383],
    windLines: [0, 2.4, -1.057],
  },
  [BoxSizeEnum.Large]: {
    smoke: [0.87, 0.43, -6.48],
    smokeTrail: [0.87, 0.43, -6.48],
    departGlow: [0, 3.36, -0.13],
    windLines: [0, 2.4, -3.4],
  },
}

/**
 * 📐 `SkidEffect/Skid` ×2 — the tyre marks a bus leaves inside the lot.
 *
 * 🔍 Two `TrailRenderer`s, switched by `Bus.ToggleSkidEmitting`
 * (`__0x20fcd08.c`, `Bus.skids` 0x118). Four call sites turn them **on** —
 * `MoveBoxToTarget`:63, `TryMoveToConveyor`:104, `DepartAndExit`:155 and the
 * inlined copies of the last — and exactly one turns them **off**:
 * `<MoveBoxToTarget>b__0` (`__0x210cb38.c`:11), the lot tween's `OnComplete`.
 * So a skid is laid for exactly as long as a bus is rolling in its slot.
 *
 * ⚠️ **They never appear on the road, and the reason is not the switch.**
 * `Bus.EnterSplineRideMode` (`__0x20fc5ac.c`) detaches every entry of `skids`,
 * `Destroy`s it after `trail.time`, and nulls the array — and
 * `BusSplineFollower.Initialize` calls it the moment a bus is admitted. So by
 * the time `DepartAndExit` reaches its `ToggleSkidEmitting(true)`, there is
 * nothing left to switch: that call is a **no-op for every spline bus**. A port
 * that read only the call sites would expect skids behind a bus leaving the
 * loop and never see one.
 *
 * The values are the prefab's, identical on both trails and on all three sizes
 * apart from where they sit.
 */
export const BUS_SKID = {
  /** `m_Time` — how long a point of the ribbon survives. */
  seconds: 0.65,
  /** `m_MinVertexDistance` — no new point until the wheel has moved this far. */
  minVertexDistance: 0.05,
  /** `widthMultiplier`, and `widthCurve` from head to tail. */
  width: 0.85,
  widthCurve: [
    [0, 1],
    [1, 0.60648143],
  ] as const,
  /** `colorGradient` — black, fading from 0.784 to nothing. Tyre rubber. */
  colour: [0, 0, 0] as const,
  alphaCurve: [
    [0, 0.78431374],
    [1, 0],
  ] as const,
  /**
   * Where the two wheels are, per size, in Unity local space. `SkidEffect`
   * itself is at the origin, so these are the children's own offsets.
   */
  anchors: {
    [BoxSizeEnum.Small]: [
      [1.75, 0.1, -2.86],
      [-1.75, 0.1, -2.86],
    ],
    [BoxSizeEnum.Medium]: [
      [1.75, 0.2, -3.68],
      [-1.75, 0.2, -3.68],
    ],
    [BoxSizeEnum.Large]: [
      [1.75, 0.2, -5.69],
      [-1.75, 0.2, -5.69],
    ],
  } as Readonly<Record<BoxSizeEnum, readonly (readonly [number, number, number])[]>>,
} as const

/**
 * 📐 `Hit_MetalExplosion.prefab` — the spark burst on a bus-to-bus collision.
 *
 * 🔍 The trigger is exact. `<HandleClicked>b__2` (`__0x210c98c.c`:30-39) is the
 * **only** place in the whole binary that constructs and publishes a
 * `BusCrashedEvent`, and it carries one field: `CrashPosition` =
 * `_movementTarget.Point`. `BusCrashVFXManager.OnBusCrashed` (`__0x2109c14.c`)
 * then does exactly four things:
 *
 *     if (_particleInstance == null) return;
 *     if (!activeInHierarchy) SetActive(true);
 *     transform.position = e.CrashPosition + Vector3.up * yOffset;
 *     _particleInstance.Play(withChildren: true);
 *
 * — no rotation is ever set, and nothing stops it: the systems are one-shot
 * (`looping: 0`) and run themselves out. `Awake` instantiates **one** instance
 * and reuses it forever, so two collisions close together restart the same
 * burst rather than stacking two.
 *
 * ❓ Two unknowns, both from AssetRipper stripping the MonoBehaviour payload:
 *
 *  - **`crashParticlePrefab` → `Hit_MetalExplosion` is inferred**, by
 *    elimination: it is the only impact-style particle prefab in the whole
 *    export (the other 68 are UI, bus, elevator, follower, queue, spline,
 *    stickman and tutorial). The tooltip on the field reads *"Çarpışmada çıkacak
 *    olan kıvılcım Particle System prefab'ı"* — "the spark ParticleSystem
 *    prefab that appears on collision" — which fits `Hit_Sparks`.
 *  - **`yOffset` is not recoverable.** `1` here is the C# field initializer from
 *    `.ctor` (`__0x2109da0.c`, `0x3f800000` at +0x28); the value the scene
 *    actually serialized would have overridden it and is gone.
 *
 * ⚠️ This fires on the **bump**, not on the lost level. `PlayFailCrash` never
 * publishes `BusCrashedEvent`, so a level you lose plays no sparks at all — it
 * plays the topple, and nothing else. Do not "helpfully" wire this to both.
 */
export const BUS_CRASH_VFX = {
  /** `BusCrashVFXManager.yOffset` — ❓ the `.ctor` default, not the scene value. */
  yOffset: 1,
  /** All three burst at t = 0 and share a cone shape, `arc: 360`. */
  systems: [
    {
      node: 'Hit_MetalExplosion',
      /** `m_Bursts[0].countCurve` — `minMaxState: 0`, so `scalar`, not 8..12. */
      burstCount: 8,
      lifetimeSeconds: [0.1, 0.15] as const,
      speed: [1, 3] as const,
      size: [0.6, 0.8] as const,
      gravityModifier: 0,
      /**
       * 🔴 `magnitude: 0` — the shards are damped to a **standstill**, 15 % of
       * their remaining speed every frame. They burst out and stop; they do
       * not fly on for the whole 0.15 s. Unmodelled, a shard at 3 u/s covers
       * five times the ground it should and the impact reads as a spray
       * rather than a hit.
       */
      speedLimit: 0,
      dampen: 0.15,
      shape: { type: 'sphere', radius: 1 } as const,
      sheet: { tilesX: 1, tilesY: 1 } as const,
      startRotation: [0, 0] as const,
      sizeCurve: [
        [0, 1],
        [0.6694215, 1],
        [0.9807162, 0.027472556],
      ] as const,
      alphaCurve: [
        [0, 1],
        [0.7735255970092317, 1],
        [1, 0],
      ] as const,
      /** `m_RenderMode: 1` is Stretch, `m_LengthScale: 4.2` — long shards. */
      renderMode: 'stretch',
      lengthScale: 4.2,
      material: 'triangle_curve_AB',
    },
    {
      node: 'Hit_Glow',
      burstCount: 1,
      lifetimeSeconds: [0.16, 0.16] as const,
      speed: [0, 0] as const,
      size: [2, 2] as const,
      gravityModifier: 0,
      speedLimit: 0.7,
      dampen: 0.09,
      shape: { type: 'sphere', radius: 0.01 } as const,
      sheet: { tilesX: 1, tilesY: 1 } as const,
      startRotation: [0, 6.283185] as const,
      sizeCurve: [
        [0, 1],
        [1, 0.6923077],
      ] as const,
      alphaCurve: [
        [0.14581521324483102, 1],
        [1, 0],
      ] as const,
      renderMode: 'billboard',
      lengthScale: 2,
      material: 'glow1_ADD',
    },
    {
      node: 'Hit_Sparks',
      burstCount: 16,
      lifetimeSeconds: [0.2, 0.6] as const,
      speed: [9, 16] as const,
      size: [0.25, 0.25] as const,
      /** The only one that falls, and it falls faster than gravity. */
      gravityModifier: 1.4,
      /** Also clamped to a standstill, harder — 20 % a frame. */
      speedLimit: 0,
      dampen: 0.2,
      shape: { type: 'sphere', radius: 0.8 } as const,
      sheet: { tilesX: 1, tilesY: 1 } as const,
      startRotation: [0, 0] as const,
      sizeCurve: [
        [0, 1],
        [1, 0],
      ] as const,
      alphaCurve: [
        [0, 0],
        [0.1282825970855268, 1],
        [0.8046692607003891, 1],
        [1, 0],
      ] as const,
      /** Stretch with `m_LengthScale: 0.1` — short, fast streaks. */
      renderMode: 'stretch',
      lengthScale: 0.1,
      material: 'circlestretchy_AB',
    },
  ] as const,
} as const



/**
 * 📐 `BusBodyMesh._sourceHalfWidth` (0x100) — how wide the body's own mesh is,
 * measured off the mesh.
 *
 * Not a `[SerializeField]`: `NormalizeSourceVertices__0x2100f78.c`:45-71 walks
 * the source vertices keeping `max(|v.x|)` and writes the result to 0x100 at
 * run time. So the only way to know it is to measure the same mesh, and the
 * three per-size middles — `BusCenter_0/1/2` — all come out at **2.1697**.
 *
 * ⚠️ `BusCenter` with no suffix is the asset pipeline's base-name fallback for
 * meshes it could not decode. It is a different, narrower mesh (1.9416), no bus
 * is built from it, and using it would put everything scaled from this number
 * 11 % low.
 *
 * `ApplyDeformedSegments` scales it twice: `* 2.1` is the radius inside which
 * the body counts as folded back on itself (`:717`), and `* 4` is the opening
 * width of the clearance channels (`:713`). `npm run check:bodyfold` is the
 * sweep that uses the first; `web/tests/busBodyFold.test.ts` locks it.
 *
 * This is 📐 rather than 🔍 for a second reason as well as the first: it is the
 * **port's** mesh that is measured. If `npm run assets` ever decoded a
 * different `BusCenter_*`, this number would move with it and nothing here
 * would notice — which is why the test that pins it reads the OBJ rather than
 * trusting the constant.
 */
export const BUS_SOURCE_HALF_WIDTH = 2.1697

/**
 * `BoxSizeExtensions.GetSplineLengthPercent` — RECOVERED, and the answer to
 * "how long is a bus on the road".
 *
 *     Small 0.05    Medium 0.075    Large 0.125
 *
 * A bus's `_busLengthWorldUnits` is **a fraction of the road**, not a fixed
 * distance: `SplineController.CheckBusRequests` calls this with the bus's size
 * and hands `percent * mainSplineLength` to `BusSplineFollower.Initialize`, and
 * `GetRequiredSpaceForBus` / `UpdateEffectiveFollowGap` size their arithmetic
 * the same way:
 *
 *     required = flowSpaceMargin + uniformFollowGap
 *              + passengerHeadExtension + passengerTailExtension
 *              + splineLength * GetSplineLengthPercent(size)
 *
 * So the middle body really is a concertina — that is what `Accommodations`,
 * `BusBodyMesh` and the "Bus Stretching Parts" header are all for. It stretches
 * to whatever the track calls for, and five buses (`SplineController..ctor`
 * writes `maxBusesOnSpline = 5`) always cover about the same share of the loop
 * whether the road is 69 units long or 233.
 *
 * Parked, the middle is its authored 2.0. On a 69-unit loop a Medium's middle
 * is 5.2, on a 233-unit loop a Large's is 29.2 — so the drawn bus is very much
 * **not** the same length as the same bus in the lot.
 */
export const BUS_SPLINE_LENGTH_PERCENT: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 0.05,
  [BoxSizeEnum.Medium]: 0.075,
  [BoxSizeEnum.Large]: 0.125,
}

/**
 * ⚠️ **A DELIBERATE DEVIATION from `BUS_SPLINE_LENGTH_PERCENT`, at the user's
 * request** — the share of road a bus claims *in this port*, as opposed to the
 * share `GetSplineLengthPercent` returns in the original.
 *
 * The complaint it answers: riders on a Small bus stand 1.11 apart while a
 * Medium packs them 0.72 apart, so a full Small reads as half-empty next to a
 * full Medium. In the shipped game every bus looks equally packed.
 *
 * Rider spacing is `(headExt + tailExt + pct * roadLength) / trunc(cap / 3)`, so
 * it is the same for all three sizes at every road length **iff both** the
 * extension total **and** `pct` are proportional to capacity. Neither was:
 *
 *                 S        M        L      S : M : L      wanted
 *     ext      4.670    6.640   10.640   0.70 : 1 : 1.60   0.5 : 1 : 1.5
 *     pct      0.050    0.075    0.125   0.67 : 1 : 1.67   0.5 : 1 : 1.5
 *
 * Medium is the anchor (its density is the one the user judged correct), so
 * both tables are re-scaled to `capacity / 36` around it and Medium keeps its
 * decompiled value. See `passengerHeadExtension` for the other half.
 *
 * ⚠️ This is NOT a correction — 0.05 / 0.075 / 0.125 are read straight out of
 * three 64-bit immediates in `BoxSizeExtensions$$GetSplineLengthPercent__0x20f6ca0.c`
 * and `tests/busGeometry.test.ts` still pins `BUS_SPLINE_LENGTH_PERCENT` to
 * them. The original really does make a Small bus proportionally longer than
 * its seat count. We are choosing to differ, and the constant above is kept so
 * the choice stays visible and reversible.
 *
 * Because the drawn bus is `headCap + busMiddleSpan + tailCap`, a Small and a
 * Large are also *drawn* shorter on the road than before — that is the point,
 * not a side effect: the cabin shrinks to fit the riders rather than the riders
 * spreading to fill the cabin.
 */
export const BUS_ROAD_LENGTH_PERCENT: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 0.0375,
  [BoxSizeEnum.Medium]: 0.075,
  [BoxSizeEnum.Large]: 0.1125,
}

/** `_busLengthWorldUnits` for a bus of this size on a road this long. */
export function busMiddleSpan(size: BoxSizeEnum, splineLength: number): number {
  return splineLength * BUS_ROAD_LENGTH_PERCENT[size]
}

/**
 * What the middle measures in the lot, where the three pieces just tile.
 *
 * 🔍 `BusSplineFollower.Initialize` (`__0x2110984.c`:413-443) seeds the bus onto
 * the entry curve **folded**, not at full length:
 *
 *     _tailPercent = splineComputer.Project(_bus.tailPart.position);
 *     _headPercent = splineComputer.Travel(
 *         _tailPercent, (_bus.headPart.position - _bus.tailPart.position).magnitude);
 *
 * and that magnitude is `BUS_JOINT_Z[size].head - BUS_JOINT_Z[size].tail`, which is
 * **2.0 on all three prefabs** (1.9999993 / 1.9999986 / 1.9999995 — the joints sit
 * at +-1 either side of the origin, to within float noise). So the first frame draws
 * nose cap + 2 units of middle + tail cap, and what lengthens afterwards is the
 * **middle**, from 2 up to `_busLengthWorldUnits`.
 */
export const BUS_PARKED_MIDDLE_SPAN = 2

/**
 * Where the drawn tail sits on the entry curve while the bus is still unfolding.
 *
 * 🔍 The entry spline starts at `Bus.tailEndPoint` (0xC0 — `dump.cs`:710437, tooltip
 * "Spline bu noktadan baslar", *the spline starts from this point*; built there by
 * `SplineController.CheckBusRequests`:238-255). `Initialize` then projects
 * `tailPart`, which lies further along by exactly the gap between the two — so the
 * drawn tail is pinned this far in, not at the curve's origin:
 *
 *     2.9326 (S) . 3.8826 (M) . 5.8804 (L)
 *
 * This is also why the tail cap reads as **full from the first frame** in the
 * original: `UpdateCapBodies` clips it as `max(0, tail - capLength)`, and the tail
 * already starts about a cap-length in.
 */
export function busParkedTailArc(size: BoxSizeEnum): number {
  return BUS_JOINT_Z[size].tail - BUS_TAIL_END_Z[size]
}

/**
 * The cabin floor, in bus-local Unity Z — the deck a rider actually stands on.
 *
 * The bus is a box with an open top: a floor plane at y 2.75-2.90 running the
 * whole length (249 vertices in `BusCenter_2` alone, and both caps carry it
 * too) and a rim at y 3.90-4.01. `Bus.OpenCover` slides the `Lids` off and that
 * floor is what you see down into.
 *
 * So riders stand **inside**, not on the roof, and the cabin is what bounds
 * them — not the drawn body, which runs on past it into the sloped bonnet and
 * the rounded tail.
 *
 *     Small   body z [-4.15, 4.26]   cabin z [-3.59, 3.09]   rim z [-3.34, 2.82]
 *     Medium  body z [-5.15, 5.46]   cabin z [-4.55, 4.08]   rim z [-4.31, 3.81]
 *     Large   body z [-7.04, 7.33]   cabin z [-6.54, 6.09]   rim z [-6.30, 5.82]
 */
export const BUS_CABIN_Z: Readonly<Record<BoxSizeEnum, { front: number; back: number }>> = {
  [BoxSizeEnum.Small]: { front: 3.09, back: -3.59 },
  [BoxSizeEnum.Medium]: { front: 4.08, back: -4.55 },
  [BoxSizeEnum.Large]: { front: 6.09, back: -6.54 },
}

/** `Parts/Head`.z and `Parts/Tail`.z — the joints the middle body spans. */
export const BUS_JOINT_Z: Readonly<Record<BoxSizeEnum, { head: number; tail: number }>> = {
  [BoxSizeEnum.Small]: { head: 1.0425895, tail: -0.9574098 },
  [BoxSizeEnum.Medium]: { head: 1.0425887, tail: -0.95740986 },
  [BoxSizeEnum.Large]: { head: 1.0504241, tail: -0.9495754 },
}

/**
 * How far riders may reach past each joint before they are out of the cabin.
 *
 * The ceiling on `passengerHeadExtension` / `passengerTailExtension`: 2.04/2.63,
 * 3.04/3.60, 5.04/5.60. Past it they are standing on the bodywork.
 */
export function busCabinExtension(size: BoxSizeEnum): { head: number; tail: number } {
  const cabin = BUS_CABIN_Z[size]
  const joint = BUS_JOINT_Z[size]
  return { head: cabin.front - joint.head, tail: joint.tail - cabin.back }
}
/**
 * Where the bus body sits along its own forward axis, relative to the prefab
 * root — the `BoxCollider`'s `m_Center.z` on the `MovementHandler` child.
 *
 * This was `-1.135 / -1.145 / -1.145` for a long time, taken from
 * `Bus_N_single.obj` on the reasoning that the mesh is what you see and the
 * collider is a padded click volume. Both halves of that were wrong:
 *
 *  - `Bus_N_single` is **not the drawn bus**. It appears once in the prefab,
 *    under `Ice/Fake`, with its renderer switched off. The bus you see is
 *    `Parts/Head` + `Middle` + `Tail`, three per-size meshes that tile
 *    together — and that assembly is centred at +0.055 / +0.155 / +0.146,
 *    within 0.08 of the collider, not 1.3 away from it.
 *  - With the collider values, `noseDistance` comes out 4.10 / 5.27 / 7.20
 *    against `HeadEndPoint` at 4.19 / 5.22 / 7.24. The two agree. Under the
 *    old numbers `HeadEndPoint` sat 1.3 units beyond the bodywork, which never
 *    made sense and was carried as an open question for a long time.
 *
 * The 1.3-unit error put every bus's art that far from its own collision box
 * and stop position.
 */
export const BUS_CENTRE_Z: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 0.11632467,
  [BoxSizeEnum.Medium]: 0.22936201,
  [BoxSizeEnum.Large]: 0.15919304,
}

/**
 * How far behind the head joint the **collider's** centre sits.
 *
 * `_headPercent` is the head joint, not the nose, so a follower's head distance
 * is not where the bumper is. The collider centre is at `BUS_CENTRE_Z` in bus
 * local space and the joint at `BUS_JOINT_Z.head`, so this is the gap between
 * them: 0.93 / 0.81 / 0.89 — a world apart from the `length / 2` you would use
 * if the head really were the nose.
 */
export function busColliderAlong(size: BoxSizeEnum): number {
  return BUS_JOINT_Z[size].head - (BUS_CENTRE_Z[size] ?? 0)
}

/**
 * `Bus.HeadEndPoint` / `TailEndPoint` — the `Head/HeadEnd` and `Tail/TailEnd`
 * children, along forward from the root.
 *
 * These bracket the bus: the span between them is 8.08 / 10.06 / 14.07 against
 * body lengths of 7.97 / 10.09 / 14.08, so they are the two bumpers. The
 * decompile names them wherever it asks where a bus starts or ends —
 * `MoveBoxToTarget` clamps the drive-out against HeadEnd, `CheckBusRequests`
 * re-reads it at admission, and `CreateEntrySpline` spans TailEnd to HeadEnd.
 *
 * They are the bumpers, and they agree with `noseDistance` once that is built
 * from the collider: 4.19 / 5.22 / 7.24 against 4.10 / 5.27 / 7.20.
 *
 * This entry has been rewritten twice. The confusion came from measuring the
 * body off `Bus_N_single`, which is the hidden `Ice/Fake` mesh rather than the
 * drawn bus, and is offset 1.3 units from everything else. Against the real
 * three-piece assembly the end points, the collider and `BUS_CENTRE_Z` all
 * line up.
 *
 * Local z of each, plus its parent, from ConveyorBox (S/M/L).prefab.
 */
export const BUS_HEAD_END_Z: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 3.1474104 + 1.0425895,
  [BoxSizeEnum.Medium]: 4.176411 + 1.0425887,
  [BoxSizeEnum.Large]: 6.1895757 + 1.0504241,
}
export const BUS_TAIL_END_Z: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: -2.9325902 - 0.9574098,
  [BoxSizeEnum.Medium]: -3.8825903 - 0.95740986,
  [BoxSizeEnum.Large]: -5.8804245 - 0.9495754,
}

/**
 * Where the roof flag sits, per prefab — `Arrow (1)`'s local y.
 *
 * ⚠️ The comment that stood here said "its SpriteRenderer did not [survive
 * IL2CPP], so the picture and the size it drew at are gone", and on that
 * footing `BUS_ARROW_SCALE` was set by eye. That premise was wrong.
 * SpriteRenderer is a **built-in** Unity component: IL2CPP strips the metadata
 * of *script* types, not the serialisation of engine ones, so the whole
 * component is sitting in the prefab and every field below is read, not
 * guessed. What is actually missing is only `Arrow (1)`'s MeshRenderer — there
 * is none, because a SpriteRenderer is not one, which is why `buildPrefab`'s
 * `DRAWN_SUBTREES` walk never saw the node.
 */
export const BUS_ARROW_HEIGHT: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 4.28,
  [BoxSizeEnum.Medium]: 4.29,
  [BoxSizeEnum.Large]: 4.36,
}

/**
 * 📐 The flag's drawn size, per prefab — `Arrow (1)`'s SpriteRenderer
 * `m_Size`, with `m_DrawMode: 1` (Sliced) on all three.
 *
 * 📐 and not 🔍: these are serialised prefab fields, so they are recovered from
 * data, and `tests/busArrow.test.ts` is the script that proves it by reading
 * the three prefabs. Only the last paragraph below is 🔍.
 *
 * ConveyorBox (S).prefab:16387, (M):14162, (L):16563. Width is 4 on every
 * size; only the length along the bus changes. The runtime never writes the
 * field back — the only thing that touches `Bus.arrow` (0x28) is
 * `GameObject.SetActive`, in `EnterSplineRideMode`:45 and the five other
 * methods that hold the offset — so these are the sizes that ship.
 */
export const BUS_ARROW_SIZE: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 3.5,
  [BoxSizeEnum.Medium]: 5,
  [BoxSizeEnum.Large]: 7.7,
}
export const BUS_ARROW_WIDTH = 4

/**
 * 📐 The sprite the flag draws, from `Sprite/Arrow_0.asset` — the file the
 * SpriteRenderer's `m_Sprite` guid (5470f170743edf14eb5f37bbc336933a)
 * resolves to on all three prefabs.
 *
 * `m_Rect` 512x512 at `m_PixelsToUnits: 100` is 5.12 units square, and
 * `m_Border` is {x: 0, y: 105, z: 0, w: 312} — left and right zero, so the
 * width scales whole and only the length is sliced into three bands: a fixed
 * 1.05-unit foot, a stretching middle, and a fixed 3.12-unit head.
 *
 * Measured against the pixels, that border lands where a 9-slice should. The
 * silhouette is a constant 150 px wide from uy 60 to uy 245; the stretch band
 * (uy 105..199) sits entirely inside that straight run, so stretching it only
 * ever lengthens the shaft and never touches the head or the rounded foot.
 */
export const ARROW_SPRITE_UNITS = 5.12
export const ARROW_BORDER_FOOT = 1.05
export const ARROW_BORDER_HEAD = 3.12


/**
 * How the body is drawn once the bus is riding a spline.
 *
 * Parked, the bus is three tiled pieces at their authored lengths. On the road
 * the original does something different: `InitCapBodies` hands the nose and the
 * tail to their own `BusBodyMesh` caps, and `UpdateMiddleBodyClip` gives the
 * middle a percent window that is **widened at both ends** before rebuilding:
 *
 *     margin  = middleBodyOverlap / splineLength
 *     start  -= margin
 *     end    += margin
 *
 * so the concertina is regenerated along the curve with its ends buried a
 * little way under the caps — that is all `middleBodyOverlap` is for
 * (*"How much the middle body extends into the head and tail meshes"*), a seam
 * hider, not a length.
 *
 * `UpdateCapBodies` then places the caps **outside** that span — the head cap
 * runs forward from `_headPercent` for `_headCapWorldLength`, the tail cap back
 * from `_tailPercent` — so the drawn bus is `headCap + bodyLength + tailCap`.
 * `bodyLength` is `busMiddleSpan` — a share of the road, not a fixed distance
 * — so the drawn bus is longer than the parked one by however far the middle
 * has stretched: 13.8 for a Medium on a 69-unit loop against 10.6 in the lot.
 * None of it needs tuning: the cap lengths are the source meshes' own and the
 * middle comes straight out of `GetSplineLengthPercent`.
 */
export interface BusBodyTuning {
  /**
   * `totalDuration` of `Bus.PlayAccommodationAnimation`, called once from
   * `BusSplineFollower.Initialize` as the bus takes to the spline.
   *
   * The sequence gives each ring a blend-shape tween lasting `0.4 × total` and
   * starts them `(total − 0.4 × total) / (count − 1)` apart, so the whole wave
   * takes `total`, the first ring clears at `0.4 × total`, and the last at `total`.
   *
   * ⚠️ It is **not** a `[SerializeField]` — the old note here said so and it is
   * wrong. `dump.cs`:709622 declares `PlayAccommodationAnimation(float
   * totalDuration)`: a plain method **parameter**, supplied by the one caller
   * (`BusSplineFollower.Initialize`:519), and Ghidra dropped the float there. No
   * field of this name exists anywhere in the dump, so there is nothing in the
   * binary left to read and the clip is the only evidence there can be.
   */
  accommodationAnimationSeconds: number
}

export const DEFAULT_BUS_BODY_TUNING: BusBodyTuning = {
  // 📐 **Measured off `reference/gameplay.mp4`, not chosen.** The blue Medium
  // leaves its slot at ~2.14 s on level 8; stepping frame by frame
  // (`npm run ref -- 2.10 2.133 2.167 2.20 2.233 2.267 --grid
  //   --crop 95x70+195+322 --scale 9`, then the same for 2.40-2.567):
  //
  //     2.100-2.133  arrow and lid on, every ring present
  //     ~2.150       arrow and lid gone
  //     ~2.250       the first ring has cleared, cabin open at the nose
  //     ~2.417       the last ring clears; body smooth from then on
  //
  // The decompile says the first ring finishes at `0.4 × total` and the last at
  // `total`, so the two readings over-determine it:
  //
  //     0.6 × total = 2.417 - 2.250 = 0.167  ->  total = 0.278
  //
  // and that in turn puts the wave's start at 2.417 - 0.278 = **2.139**, which is
  // the frame the lid comes off — a corroboration that was not used to derive it.
  // At 30 fps each reading carries +-1 frame, giving total in **[0.24, 0.32]**.
  //
  // The old value was **0.9**, which the clip rules out flatly: at 0.9 the wave
  // would still be running at 3.0 s, and the bus is smooth by 2.44. It also left
  // the rings morphing long after the body had finished stretching (the unfolding
  // measures ~0.12 s), so a bus reached full length still wearing its ribs.
  // Locked by `accommodationWave.test.ts`.
  accommodationAnimationSeconds: 0.28,
}

/**
 * `BusMovementHandler.raycastPoints` — the `RayPoints/RaycastPoint1..3`
 * children of the bus prefab. Three rays across the front; the parent's local
 * z varies by size, the children spread ±1.5 across the width.
 *
 * Origins sit inside the bus's own collider on purpose: Unity's raycast does
 * not report a collider the ray starts inside, so the bus never blocks itself.
 * We exclude self explicitly instead.
 */
const RAY_PARENT_Z: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 1.6,
  [BoxSizeEnum.Medium]: 3.1,
  [BoxSizeEnum.Large]: 4.6,
}
/**
 * ❓ The **order** of these is a guess, and it is the only thing about them that
 * is. The offsets themselves are read off `ConveyorBox (S|M|L).prefab`
 * (`RaycastPoint1` = `x: 1.5`, `2` = `0`, `3` = `-1.5`), but what `HandleClicked`
 * enumerates is `BusMovementHandler.raycastPoints`, a serialized
 * `List<Transform>` (`dump.cs`:710291) whose element order lives in the prefab's
 * MonoBehaviour payload — and AssetRipper exported every MonoBehaviour in this
 * bundle as an empty stub. Name order is the assumption. It matters only for
 * which ray wins a tie; see `scanForward`'s `RAY_TIE_EPSILON`.
 */
export const RAY_SPREAD_X = [1.5, 0, -1.5] as const

export function raycastPointsFor(size: BoxSizeEnum): Vector3[] {
  const z = RAY_PARENT_Z[size]
  return RAY_SPREAD_X.map((x) => new Vector3(x, 0.75, z))
}

/**
 * Passenger capacity per bus size — **18 / 36 / 54**, not the 12 / 24 / 36 the
 * `Bus.capacityOverride` tooltip names as the built-in default.
 *
 * The tooltip describes the fallback used when `capacityOverride` is 0; the
 * shipped prefabs evidently set it. The level data settles which value is live,
 * because the win condition is `AllQueuesClearedEvent` — every queued passenger
 * has to board — and that only closes if total seats equal total passengers.
 *
 * Checked against all 40 levels: with 18/36/54 the seat count equals the
 * passenger count **exactly** on 39 of them. With 12/24/36 every level ships
 * 1.5× more passengers than seats, so no level could ever be cleared.
 *
 * The odd one out is level 14, short by exactly one Medium (432 seats vs 468
 * passengers). Its second elevator's top layer holds 2 buses where every other
 * layer in the level holds 3, so the missing bus most likely comes from the
 * elevator logic rather than from a wrong capacity here.
 */
export const BUS_CAPACITY: Readonly<Record<BoxSizeEnum, number>> = {
  [BoxSizeEnum.Small]: 18,
  [BoxSizeEnum.Medium]: 36,
  [BoxSizeEnum.Large]: 54,
}

// ---------------------------------------------------------------------------
// Prefab-derived layout — RECOVERED
// ---------------------------------------------------------------------------

/** `WaitingArea.prefab` → `BoardingPoint`. Where a bus stops on the road. */
export const WAITING_AREA_BOARDING_POINT = new Vector3(0, 0, -7.2)

/** `PeopleQueue.prefab` → `BoardingPoint`. Head of the queue. */
export const QUEUE_BOARDING_POINT = new Vector3(0, 0, 2.5)

/**
 * `PeopleQueue.prefab` → `Path/Point_0..Point_10`, in queue-local space,
 * ordered head-first. People walk down this toward the boarding point.
 */
export const QUEUE_PATH: readonly Vector3[] = [
  new Vector3(0, 0.2, 1.5),
  new Vector3(0, 0.2, 2.58),
  new Vector3(0.32, 0.2, 3.42),
  new Vector3(0.71, 0.2, 4),
  new Vector3(1.42, 0.2, 5),
  new Vector3(2.25, 0.2, 6),
  new Vector3(3.48, 0.2, 7),
  new Vector3(5.51, 0.2, 8),
  new Vector3(7.65, 0.2, 8.21),
  new Vector3(10, 0.2, 8.21),
  new Vector3(30.1, 0.2, 8.21),
]

// ---------------------------------------------------------------------------
// Movement — RECOVERED constants
// ---------------------------------------------------------------------------

/**
 * 🔍 `BusMovementHandler.CalculateAnimationDuration` returns
 * `Vector3.Distance(target, transform.position) / 24.64`, so a bus leaving the
 * parking lot moves at a flat 24.64 world units per second.
 *
 * The whole body, read in full:
 *
 *     if (_targetTransform != null)                       // 0x78
 *         return (target - _targetTransform.position).magnitude / 24.64f;
 *
 * **There is no floor.** The port used to wrap all three call sites in
 * `Math.max(..., 0.05)`, a number nothing in the binary asks for, and measured
 * over 40 levels it bound on **117 of 422** lot moves (27.7%): every move
 * shorter than 0.05 * 24.64 = 1.232 units was stretched, by up to 2.5x on the
 * shortest ones. It was not protecting anything either — `TweenRunner` already
 * clamps `Math.max(duration, 1e-6)` internally (`tween.ts`:114), so a
 * zero-length move cannot divide by zero with or without it.
 */
export const BUS_DISPATCH_SPEED = 24.64

/**
 * 🔍 `BusSplineFollower.LateUpdate` (`__0x210cc5c.c` lines 59-65), the whole of
 * the `IsDeparting` branch:
 *
 *     if (IsDeparting) {
 *         if (!_exitCompleted && _cachedCurrentSplineLength >= 1f) {
 *             float step = (_worldSpeed * 1.68f) / _cachedCurrentSplineLength;
 *             _headPercent += step * Time.deltaTime;
 *             ...
 *             return;                       // <- never reaches the traffic block
 *
 * A hard-coded literal, not a serialized field — there is nothing on
 * `SplineController` to override it. `step` is percent-per-second along the
 * **exit** spline, so in world units it is a flat `_worldSpeed * 1.68`.
 *
 * The `return` matters as much as the number: a bus running out is the one
 * thing on the road that `ComputeTrafficSpeedFactor` never slows down. It
 * leaves at 45.16 u/s and drives straight through whatever is ahead of it,
 * because by then it is on its own throwaway curve and nothing else is.
 */
export const DEPART_SPEED_MULTIPLIER = 1.68

/** `SplineController.FollowerSpacing`, a private const. */
export const FOLLOWER_SPACING = 1.3

/** `SplineController.SplineLoopAnimationDuration`, a private const. */
export const SPLINE_LOOP_ANIMATION_DURATION = 0.054

// ---------------------------------------------------------------------------
// PLACEHOLDERS — every one of these was a [SerializeField]
// ---------------------------------------------------------------------------

export interface GameplayTuning {
  /** `SplineController.maxBusesOnSpline` */
  maxBusesOnSpline: number
  /** `SplineController.uniformFollowGap` — target visible gap between buses. */
  uniformFollowGap: number
  /** `SplineController.minUniformFollowGap` — floor when traffic compresses. */
  minUniformFollowGap: number
  /** `SplineController.entrySpeedMultiplier` — speed factor while merging. */
  entrySpeedMultiplier: number
  /** `SplineController.followSpeedRamp` — distance over which speed ramps in. */
  followSpeedRamp: number
  /** `SplineController` 0x48 — per-bus slack in `UpdateEffectiveFollowGap`. */
  targetFlowSlackPerBus: number
  /**
   * `SplineController.entryWindowClearance` — "Giriş penceresine yolcu
   * taşmalarının üstüne eklenen ekstra tampon": extra buffer added on top of
   * the passenger overhangs when sizing the entry window.
   *
   * "Passenger overhangs" is the tooltip naming `passengerHead/TailExtension`
   * for what they are — how far the **riders** reach, not how far the bodywork
   * does. This is what covers the difference: the sloped nose and the rounded
   * tail nobody stands on, on both buses at once, since
   * `IsSpaceClearForBus` adds it only to the merging bus's own window.
   *
   * Swept over levels 1/2/3/5/8/11/14/20/25: at 1.5 six frames of bus-on-bus
   * overlap, at 2.5 three, at 3 a single grazing frame, and 3.5 and 4 no better
   * — so the rest is the merge sweep itself, not admission. `solve` reports
   * 14/26 at every setting.
   */
  entryWindowClearance: number

  /**
   * `SplineController.flowSpaceMargin` (0x3C) — "Otobüs başına alan bütçesine
   * eklenen akış marjı": the flow margin added to each bus's space budget.
   * Read only by `GetRequiredSpaceForBus`.
   */
  flowSpaceMargin: number

  /** Cruise speed along the road. Not a named field in the dump; see note. */
  splineSpeed: number

  /** `QueueController.busStopForwardOffset` */
  busStopForwardOffset: number
  /** `QueueController.boardingInterval` — seconds between passengers. */
  boardingInterval: number
  /** `QueueController.boardingJumpDuration` */
  boardingJumpDuration: number
  /** `QueueController.boardingJumpPower` */
  boardingJumpPower: number
  /** `QueueController.trayReleaseInterval` */
  trayReleaseInterval: number
  /** `QueueController.boardingWalkSpeed` */
  boardingWalkSpeed: number

  /** `BusMovementHandler.crashStopDistance` — gap left when a bus is blocked. */
  crashStopDistance: number

  // -- passengers riding the bus ------------------------------------------
  /**
   * `BusSplineFollower.passengerTopOffset`, whose tooltip in the IL2CPP dump
   * reads: *"Height above the spline where boarded passengers stand (top of the
   * bus body)."*
   *
   * The deck they stand on is the **cabin floor**, not the roof. The body
   * carries a floor plane at y 2.75-2.90 running its whole length — 249
   * vertices on the centreline of `BusCenter_2` alone, and both caps have it —
   * under a rim at y 3.90-4.01. `Bus.OpenCover` slides the `Lids` off and that
   * floor is what you see down into.
   *
   * At 2.8 a rider (2.32 tall at prefab scale) clears the rim by half a head,
   * which is what makes a full bus read as a crowd of heads in a box rather
   * than a pile of bodies on a roof — and it is the only thing that makes the
   * packing work, because the row spacing is fixed by the cabin length.
   *
   * Still a `[SerializeField]`, so it stays a HUD slider.
   */
  passengerTopOffset: number
  /** `BusSplineFollower.passengerSideOffset` — column offset from the centreline. */
  passengerSideOffset: number
  /**
   * `Bus.passengerHeadExtension` — *"How many world units forward into the Head
   * Part passengers can occupy."*
   *
   * **Per bus prefab, not global.** The field lives on `Bus` (0xD4), so each of
   * `ConveyorBox (S/M/L)` carries its own value, and every reader takes it off a
   * specific bus: `GetPassengerSlotPose` off its own, `IsSpaceClearForBus` and
   * `ComputeTrafficSpeedFactor` off both buses in the pair. Keyed by size here
   * for the same reason.
   *
   * It matters most for **rider density**, which is the only thing that pins it
   * down visually:
   *
   *     rowSpacing = (tailExtension + headExtension + bodyLength) / (capacity / 3)
   *
   * With one value shared across all three sizes the small bus comes out far
   * sparser than the others, because the fixed sum is spread over 6 rows instead
   * of 12 or 18. See `DEFAULT_GAMEPLAY_TUNING` for the arithmetic.
   */
  passengerHeadExtension: Record<BoxSizeEnum, number>
  /** `Bus.passengerTailExtension` — units back into the tail part. Per prefab. */
  passengerTailExtension: Record<BoxSizeEnum, number>

  /** `QueueController.maxRowsPerBatch` — rows pulled from one queue at a time. */
  maxRowsPerBatch: number

  // -- WaitingArea ---------------------------------------------------------
  /**
   * `WaitingArea.maxOccupants` — "Maximum number of characters the area can
   * hold". The knob the whole puzzle turns on: a room crowded with colours no
   * bus wants is how a level deadlocks.
   */
  waitingAreaMaxOccupants: number
  /** `WaitingArea.areaBoundsSize` — width (X) and depth (Z) of the room. */
  waitingAreaBounds: { x: number; y: number }
  /** `WaitingArea.jumpDuration` — "Duration of the entry walk into the area". */
  waitingAreaWalkInDuration: number
  /** `WaitingArea.flowTargetXZ.x` — where a single-colour crowd gathers. */
  waitingAreaFlowTargetX: number
  /** `WaitingArea.doorWidth` — "narrower = more squeeze". Clamps entry X. */
  waitingAreaDoorWidth: number
  /** `WaitingArea.doorLocalZ` — local Z of the entry door. */
  waitingAreaDoorLocalZ: number
  /** `WaitingArea.walkInDistance` — how far past the door people land. */
  waitingAreaWalkInDistance: number
  /** `WaitingArea.exitDoorLocalZ` — where passengers leave to board. */
  waitingAreaExitDoorLocalZ: number
  /** `WaitingArea.exitDoorWidth` — width of the exit opening. */
  waitingAreaExitDoorWidth: number
  /** `WaitingArea.doorOpenSlideDistance` — how far each exit leaf slides sideways. */
  waitingAreaDoorOpenSlideDistance: number
  /** `WaitingArea.doorOpenSlideZ` — how far each exit leaf retreats in Z first. */
  waitingAreaDoorOpenSlideZ: number

  // -- WaitingArea kinematic crowd (`LateUpdate`) ---------------------------
  /** `WaitingArea.characterRadius` — half the space a passenger takes up. */
  waitingAreaCharacterRadius: number
  /** `WaitingArea.skinWidth` — "Extra gap beyond 2×radius to prevent overlap". */
  waitingAreaSkinWidth: number
  /** `WaitingArea.flowSpeed` — "How fast characters try to walk towards the front". */
  waitingAreaFlowSpeed: number
  /** `WaitingArea.flowTargetXZ.y` — the Z half of the point the crowd drifts to. */
  waitingAreaFlowTargetZ: number
  /** `WaitingArea.smoothTime` — "How quickly visuals catch up to sim positions". */
  waitingAreaSmoothTime: number
  /** `WaitingArea.overlapIterations` — `[Range(1, 10)]` separation passes a frame. */
  waitingAreaOverlapIterations: number
  /** `WaitingArea.tiltMultiplier` — "How much they tilt based on movement speed". */
  waitingAreaTiltMultiplier: number
  /** `WaitingArea.maxTilt` — "Maximum allowed tilt in degrees". */
  waitingAreaMaxTilt: number
  /** `WaitingArea.wobbleStiffness` — "Higher = faster wobble". */
  waitingAreaWobbleStiffness: number
  /** `WaitingArea.wobbleDamping` — "How quickly the wobble dampens/stops". */
  waitingAreaWobbleDamping: number
  /** `WaitingArea.stopWobbleBoost` — extra energy injected the moment they stop. */
  waitingAreaStopWobbleBoost: number
  /** `WaitingArea.maxWobbleDuration` — seconds before a standing wobble settles. */
  waitingAreaMaxWobbleDuration: number
  /** `WaitingArea.baseFacingAngle` — "180 means they face Z− (towards camera)". */
  waitingAreaBaseFacingAngle: number
  /** `WaitingArea.randomRotationOffset` — Y spread applied after the walk-in. */
  waitingAreaRandomRotationOffset: number

  // -- PeopleQueue ---------------------------------------------------------
  //
  // 🔍 The whole block below is `PeopleQueue$$.ctor__0x2121640.c`, which writes
  // six fields in three 8-byte stores (lines 18-20). Each store puts its LOW
  // half at the named offset and its high half four bytes on, so paired with
  // the offsets in `dump.cs:711433-711449` it reads, in address order:
  //
  //     0x38 spacing           0x3FB33333   1.4     "arc distance between two rows"
  //     0x3C samplesPerSegment 0x00000008   8
  //     0x40 advanceSpeed      0x40D70A3D   6.72    "world units per second"
  //     0x44 characterScale    0x3FC66666   1.55
  //     0x48 laneCount         0x00000003   3
  //     0x4C laneSpacing       0x3FB33333   1.4     "between adjacent lanes"
  //
  // Note that the first store's literal reads `0x83fb33333` — nine digits, so
  // the leading `8` is the *high* half and 0x3C is what it lands in. Dropping
  // the short store's implicit zeros is how a block like this gets misread.
  //
  // Three of the six are confirmed independently at their read sites:
  //
  //     ApplyPose:85            laneSpacing * (slot + (laneCount - 1) * -0.5)
  //     RetargetEntry:66-68     Mathf.Max(0.01f, advanceSpeed)
  //     EnsurePathSampled:281   n = samplesPerSegment; if (n < 2) n = 1;
  //                             t = i / n
  //
  /** `PeopleQueue.laneCount` — "Number of side-by-side lanes". */
  queueLaneCount: number
  /** `PeopleQueue.laneSpacing` — "Lateral distance between adjacent lanes". */
  queueLaneSpacing: number
  /** `PeopleQueue.spacing` — "Arc distance between two rows on the path". */
  queueRowSpacing: number
  /** `PeopleQueue.advanceSpeed` — "How fast the line flows forward". */
  queueAdvanceSpeed: number
  /** `PeopleQueue.samplesPerSegment` for the Catmull-Rom path. */
  queueSamplesPerSegment: number
  /**
   * `PeopleQueue.characterScale` — "Uniform scale applied to every spawned
   * person, no matter when they spawn".
   *
   * Applied to the queue's prefab root, and a passenger keeps it for life: the
   * waiting area and the bus never touch scale, so this is the size of every
   * character in the game.
   *
   * 🔍 `.ctor` 0x44 = 1.55, and it only makes sense **paired** with
   * `passengerSideOffset` = 0.9 (`BusSplineFollower$$.ctor__0x2114d80.c`, 0x24):
   * at 1.55 a body is 1.92 wide, so the outer roof column reaches 0.9 + 0.96 =
   * 1.86 against a half-roof of 1.971. With the port's old 1.15 it reached 2.11
   * and hung over the side of every bus. That pairing is why this number sat at
   * 1 behind task `q2` for so long; both halves are read values, and they have
   * now landed together.
   *
   * The old justification for holding it at 1 — `Stickman`'s `Char (1)` child
   * is scaled 0.4576, putting a body at 1.24, "near enough `queueLaneSpacing`"
   * — was inference dressed as a reading, and the original frame contradicts
   * it: at 1.24 the belt shows between queue rows 1.4 apart, and in the frame
   * it does not. See the value site.
   */
  queueCharacterScale: number
}

/**
 * `PeopleQueue.MaxVisible` — RECOVERED, not a serialized field.
 *
 * `get_MaxVisible__0x211e8c0`, verbatim:
 *
 *     fVar3 = _pathLength / spacing;
 *     iVar1 = (int)fVar3;
 *     iVar2 = 1;  if (1 < iVar1 + 1) iVar2 = iVar1 + 1;   // Mathf.Max(1, (int)q + 1)
 *     if ((float)(int)fVar3 == INFINITY) iVar2 = 1;       // divide by zero guard
 *     return laneCount * iVar2;
 *
 * Everyone past that waits in `_pendingColors` and is spawned by
 * `FillFromPending` as the line drains.
 *
 * The divide-by-zero guard is the original's own, and it is reproduced rather
 * than replaced by clamping the divisor: clamping would return an enormous row
 * count for `spacing == 0` where the original returns one row.
 */
export function queueMaxVisible(
  pathLength: number,
  spacing: number,
  laneCount: number,
): number {
  const truncated = Math.trunc(pathLength / spacing)
  const rows = Number.isFinite(truncated) ? Math.max(1, truncated + 1) : 1
  return laneCount * rows
}

export const DEFAULT_GAMEPLAY_TUNING: GameplayTuning = {
  // A ceiling, not the real limit. 16 buses at ~11 long plus a 3.5 gap need
  // 232 units and the roads are ~176, so what actually governs admission is
  // `IsSpaceClearForBus` — physical room on the spline. `SplineController`
  // agrees: it carries `GetRequiredSpaceForBus`, `flowSpaceMargin` and
  // `targetFlowSlackPerBus`, all length-based.
  //
  // RECOVERED: `SplineController$$.ctor` writes `(this + 0x38) = 5`, and 0x38
  // is the count `UpdateEffectiveFollowGap` compares against for its
  // road-is-full case. 16 here was a guess, made back when 5 appeared to starve
  // the road — but that was before the follow gap was dynamic, so buses could
  // not pack in and a small cap really did deadlock.
  maxBusesOnSpline: 5,
  uniformFollowGap: 3.5,
  // RECOVERED from the field initialisers in `SplineController$$.ctor`, which
  // is where these live even though the fields are `[SerializeField]`:
  //
  //     (this + 0x38) = 5                     int
  //     (this + 0x3c) = 0x4060000040200000    0x3c = 2.5f, 0x40 = 3.5f
  //     (this + 0x44) = 0x4000000040200000    0x44 = 2.5f, 0x48 = 2.0f
  //     (this + 0x4c) = 0x3fc00000            1.5f
  //     (this + 0x54) = 0x406000003fa4dd2f    0x54 = 1.288f, 0x58 = 3.5f
  //
  // The offsets are pinned by the code that reads them: 0x40/0x44 clamp the
  // target in `UpdateEffectiveFollowGap`, 0x48 is its per-bus slack, 0x4c is
  // the ramp `ComputeTrafficSpeedFactor` divides by (whose own fallback is the
  // same 1.5), 0x58 is `_effectiveFollowGap` (fallback 3.5), and 0x38 is the
  // count `UpdateEffectiveFollowGap` compares against for its all-full case.
  //
  // These are C# field initialisers, so a scene could still override them —
  // but they beat guessing, and every one that was already pinned another way
  // agrees.
  minUniformFollowGap: 2.5,
  // 0x3c in the same block. Pinned by `GetRequiredSpaceForBus`, which is the
  // only reader: `flowSpaceMargin + uniformFollowGap + head + tail + len*pct`.
  flowSpaceMargin: 2.5,
  /**
   * 🔍 **1.288**, out of the same `SplineController..ctor` field block at 0x54:
   *
   *     *(undefined8 *)(param_1 + 0x54) = 0x406000003fa4dd2f;
   *
   * — 0x54 = 0x3fa4dd2f = 1.288, 0x58 = 0x40600000 = 3.5 (the initial
   * `_effectiveFollowGap`). `BusSplineFollower.LateUpdate` line 316 hard-codes
   * the very same 1.288 as its fallback for a null controller, which is the
   * usual sign that a serialized default got inlined:
   *
   *     float entryMul = 1f;
   *     if (_state == BusSplineState.Entering)
   *         entryMul = (_controller == null) ? 1.288f
   *                                          : _controller.entrySpeedMultiplier;
   *
   * It is **greater than one**: a bus merging out of the lot moves *faster*
   * than road speed, not slower. The port had **0.75** — a PLACEHOLDER from
   * before this block was decoded, and it made merging drag.
   *
   * Seven of the eight fields this `.ctor` block writes were already pinned
   * another way and all seven agree (`maxBusesOnSpline` 5, `flowSpaceMargin`
   * 2.5, `uniformFollowGap` 3.5, `minUniformFollowGap` 2.5,
   * `targetFlowSlackPerBus` 2, `followSpeedRamp` 1.5, and 0x58 = 3.5). This was
   * the only one still guessed. Locked in `tests/splineCtor.test.ts`.
   */
  entrySpeedMultiplier: 1.288,
  followSpeedRamp: 1.5,
  targetFlowSlackPerBus: 2,

  // RECOVERED. `CheckBusRequests` calls
  // `BusSplineFollower.Initialize(bus, entry, main, busLengthWorldUnits,
  // worldSpeed, ...)` with `worldSpeed` as the literal 0x41d70a3d = 26.88.
  // Not a serialized field at all — a hard-coded immediate, so this is the
  // real cruise speed. The earlier guess of 12 made everything crawl.
  splineSpeed: 26.88,

  // RECOVERED from the field initialisers in `QueueController$.ctor`
  // (`__0x2123adc.c` lines 19-22). All four stores, in offset order:
  //
  //     (this + 0x30) = 0x00000003_3e374bc7  0x30 = 0.179f,  0x34 = int 3
  //     (this + 0x38) = 0x3e645a1d3d916873   0x38 = 0.071f,  0x3c = 0.223f
  //     (this + 0x40) = 0x4020000040800000   0x40 = 4.0f,    0x44 = 2.5f
  //     (this + 0x48) = 0x4000000041866666   0x48 = 16.8f,   0x4c = 2.0f
  //
  // Field names from `dump.cs` 711616-711632: trayReleaseInterval 0x30,
  // maxRowsPerBatch 0x34, boardingInterval 0x38, boardingJumpDuration 0x3c,
  // boardingJumpPower 0x40, boardingWalkOutDistance 0x44, boardingWalkSpeed
  // 0x48, busStopForwardOffset 0x4c.
  //
  // ⚠️ The first store, and with it the two fields this port guessed longest,
  // was **missed** by the pass that decoded the rest of this block. Only 0x38
  // onwards were read; 0x30 and 0x34 were left at their guesses (0.25 and 2)
  // while the comment above them said the block had been recovered. Reading a
  // `.ctor` means reading **every** store in it.
  //
  // Each field is taken only where it has a read site:
  //
  //   0x30 — `ReleaseFromTrays` (`__0x2121994.c`:135) ends with
  //          `*(this + 100) = *(this + 0x30)`, i.e. `_releaseCooldown` (0x64)
  //          is re-armed with it after every batch. **0.179**, not the 0.25
  //          guessed here, so the queue feeds the platform ~40 % faster.
  //
  //   0x4c — `LevelLoaded` reads it as the stop's forward offset. **2.0**, not
  //          the 0.5 guessed before. It shifts the stop two units further along
  //          the road, so a bus (held the instant its nose covers the stop)
  //          comes to rest with the platform two units back down its side
  //          instead of level with its nose.
  //   0x38 — `UpdateBoarding` adds it to the boarding cooldown. **0.071**.
  busStopForwardOffset: 2,
  boardingInterval: 0.071,
  // RECOVERED from the same `.ctor` block, by finding the read sites (T5):
  //
  //   0x3c / 0x40 — `PerformBoardingJump` does
  //                 `DOJump(target, this[0x40], 1, this[0x3c])`, i.e. jump
  //                 power **4.0** and duration **0.223**. The port had 1.6 and
  //                 0.32, both guesses from before the block was decoded.
  //   0x48        — `BoardOnePerson` does `DOMove(door, distance / this[0x48])`,
  //                 so the walk-out speed is **16.8** units/second, not the 6
  //                 guessed here. At 6 a passenger crossing a 4-unit platform
  //                 took 0.67 s; the real figure is 0.24 s.
  //
  //   0x44 = 2.5  — still has no read site anywhere in the decomp. Left alone
  //                 rather than assigned by guesswork.
  boardingJumpDuration: 0.223,
  boardingJumpPower: 4,
  // 🔍 `QueueController$.ctor` 0x30. A **simulation** field, which is the side
  // of the line where this project trusts the `.ctor` — see the `[Header]` rule
  // in checklist §4. It cannot be cross-checked against the prefab: AssetRipper
  // exported every `MonoBehaviour` in this bundle as an empty stub, so no prefab
  // in `assets_assetripper/` carries a single serialized field to override it.
  trayReleaseInterval: 0.179,
  boardingWalkSpeed: 16.8,

  crashStopDistance: 0.35,

  // 🔍 `QueueController$.ctor` 0x34 — the **high half** of the same 8-byte
  // store that carries `trayReleaseInterval`, `0x00000003_3e374bc7`, so it is
  // the int 3. Read site: `ReleaseFromTrays__0x2121994.c`:158
  //
  //     iVar8 = queue.laneCount(0x48) * this.maxRowsPerBatch(0x34);
  //     if (iVar8 > area.FreeSpotCount) iVar8 = area.FreeSpotCount;
  //
  // so a batch is 3 lanes x 3 rows = **9** people, not the 6 guessed here.
  //
  // ⚠️ The old value came from an inference that does not hold: every colour
  // run in the 40 levels is a multiple of 6, so 6 was taken to be the batch.
  // It cannot be. `ReleaseFromTrays` **breaks out of the batch the moment the
  // front colour changes** (`__0x2121994.c`:131 `if (iVar8 != iVar1) break`),
  // so a batch never straddles a colour boundary and the run lengths carry no
  // information about its size at all. The 6-grid is an authoring property of
  // the levels — see `RUN_GRID` in `scripts/invariants.ts`, which is where that
  // fact now lives, separately from this one.
  maxRowsPerBatch: 3,

  // The cabin floor is at 2.8 and the rim at 4.01.
  passengerTopOffset: 2.8,
  // 🔍 `BusSplineFollower$$.ctor__0x2114d80.c`:21 —
  // `*(undefined8 *)(param_1 + 0x20) = 0x3f66666640d9999a`, whose **high** half
  // 0x3f666666 = 0.9 lands at 0x24, which `dump.cs:710462` names
  // `passengerSideOffset`. Read, not derived.
  //
  // Was 1.15, inferred from the mesh: body 3.94 wide, character 1.24, so three
  // columns "just fit" at ±1.15. That inference was sound only while the
  // character was 1.24 wide — i.e. only while `queueCharacterScale` was held at
  // 1. With the scale at its read value 1.55 a rider is 1.92 across and the
  // outer column reaches 1.15 + 0.96 = 2.11 against a half-roof of 1.971, so
  // every bus in the game wears its outer passengers over the side. At 0.9 the
  // reach is 0.9 + 0.96 = 1.86 and they sit inside the rim.
  //
  // This is the `q2` pair the ledger has been holding: both numbers are 🔍 in
  // their own `.ctor`, and they are only coherent **together**. Landed together.
  passengerSideOffset: 0.9,
  // As far as the **cabin floor** reaches past each joint — `busCabinExtension`.
  //
  // Not the cap lengths: the drawn bus runs on past the cabin, the nose sloping
  // away to the bumper and the tail rounding off, so bounding riders by the
  // drawn extent stands the front row on the bonnet and hangs the back row off
  // the tail. The cabin floor is the deck they stand on.
  //
  // `bodyLength` here is `BUS_MIDDLE_SPAN` — 2.0 on every prefab — so these two
  // are the whole difference between a Small bus and a Large one, in three
  // places at once:
  //
  //   - the cabin the riders stand in, `[-head, 2 + tail]`;
  //   - the road a bus claims in `IsSpaceClearForBus`, `tail + 2 + head`;
  //   - the gap `ComputeTrafficSpeedFactor` demands, `head + gap + tail`, which
  //     is what turns a joint-to-joint distance back into bumper to bumper.
  //
  // Rider spacing falls out as `(tail + head + 2) / trunc(capacity / 3)`, which
  // is just the cabin divided by the row count:
  //
  //     Small  6.67 /  6 = 1.11
  //     Medium 8.64 / 12 = 0.72
  //     Large  12.64 / 18 = 0.70
  //
  // against a character 1.24 wide. A full Medium or Large bus is a solid mass
  // of people and **nothing can change that**: 36 seats three abreast is 12
  // rows, the cabin is 8.6 long, and letting them spill onto the bodywork buys
  // at most 0.88. That is what 36 passengers on a 10.6-unit bus means. What
  // makes it read is `passengerTopOffset` — they stand *inside* the bus, so
  // only heads clear the rim.
  //
  // `IsSpaceClearForBus` falls back to 2.5 when the Bus is missing, which is
  // the only hint at the shipped numbers and lands in the middle of this range.
  // Still `[SerializeField]`s that did not survive: all six are HUD sliders.
  // ⚠️ **S and L DEVIATE from the prefab, at the user's request; M does not.**
  //
  // The values the cabin actually measures are 2.04/2.63, 3.04/3.60, 5.04/5.60 —
  // still returned by `busCabinExtension(size)`, which is where the ceiling
  // above is read from, and which the HUD sliders still clamp to. What changed
  // is only where the riders are *allowed* to stand, and with them the road a
  // bus claims.
  //
  // Same argument as `BUS_ROAD_LENGTH_PERCENT`: spacing is uniform across sizes
  // only if the extension total is proportional to capacity, so each size's
  // total is re-scaled to `6.64 * capacity / 36` while keeping that prefab's own
  // head:tail asymmetry (S 2.04:2.63, L 5.04:5.60 — the nose cabin is shorter
  // than the tail cabin on every bus, and that survives).
  //
  //             total was   total now   factor
  //     S           4.670       3.320    0.7109
  //     M           6.640       6.640    1        (anchor — unchanged)
  //     L          10.640       9.960    0.9361
  //
  // Both new totals are **under** the cabin ceiling, so no rider ends up on the
  // bodywork; `npm run check:passengers` is the check that says so.
  passengerHeadExtension: {
    [BoxSizeEnum.Small]: 1.45,
    [BoxSizeEnum.Medium]: 3.04,
    [BoxSizeEnum.Large]: 4.718,
  },
  passengerTailExtension: {
    [BoxSizeEnum.Small]: 1.87,
    [BoxSizeEnum.Medium]: 3.6,
    [BoxSizeEnum.Large]: 5.242,
  },
  entryWindowClearance: 3,

  // 🔍 `.ctor` 0x70 = **94**, and it is an `int`, so it decodes cleanly:
  // `*(undefined8 *)(param_1 + 0x6c) = 0x5e3df5c28f` writes eight bytes, the
  // low four being `smoothTime` = 0x3df5c28f = 0.12 and the high four being
  // `maxOccupants` = 0x5e = 94.
  //
  // Was 72, a guess ("twelve batches of six"), and 24 before that. 94 is in the
  // same country as the frame the earlier note appealed to — "somewhere near
  // eighty people on the plate".
  waitingAreaMaxOccupants: 94,
  // 📐 RECOVERED from `BusStationGround`, the mesh passengers actually stand
  // on: it spans x [-10.096, 10.096] and z [-7.293, 1.509], so 20.19 by 8.80.
  //
  // Those z figures are in `BusStation`'s space; the parent adds +2.64, so in
  // the root-local space this rectangle is clamped in, the floor runs
  // z ∈ [-4.653, 4.149] — 8.80 deep, centred 0.25 forward of the origin rather
  // than on it. The centred rectangle therefore overhangs the floor by 0.25 at
  // the back and falls 0.25 short of it at the front. Kept as measured: the
  // original clamps a centred rectangle too, so the 0.25 is the original's own
  // slop, not the port's.
  //
  // The `.ctor` field initialiser says (14, 6.1), which matches nothing in the
  // shipped prefab — not this mesh, not `BasePlate` (7.6 x 6.2), not the
  // `BoxCollider` (7.6 x 6.2). See the stale-block warning in checklist §4.
  //
  // Not from `BasePlate`. That node is scaled (7.6, 0.1, 6.2) and looks
  // authoritative, but it is switched **off** in the prefab — an editor helper
  // left behind, a third of the real width. Taking its scale as the bounds is
  // what penned the crowd into the middle of the platform.
  waitingAreaBounds: { x: 20.2, y: 8.8 },
  // 🔍 `WaitingArea.jumpDuration` (0x98) = 0.3, from `.ctor`'s
  // `0x406000003e99999a` -> low half 0x3e99999a. Not a guess about pacing:
  // `Receive` ends with `DOLocalMove(x, 0.2f, z, jumpDuration)`
  // (`__0x2134d24.c` line 174, the duration argument is `param_1 + 0x98`), so
  // this field *is* the entry-walk duration. The tooltip agrees: "Duration of
  // the entry walk into the waiting area (formerly jump duration)".
  //
  // Was 0.45, a guess — the comment already named `jumpDuration` as the source
  // but not its value.
  waitingAreaWalkInDuration: 0.3,

  // The entry side of the room, all three read by `Receive`:
  //
  //     targetX = _activeColors.Count < 2
  //             ? flowTargetX
  //             : Mathf.Lerp(areaBoundsSize.x * -0.4f, areaBoundsSize.x * 0.4f,
  //                          Mathf.Clamp01(IndexOf(color) / (Count - 1)));
  //     targetX += Random.Range(-0.8f, 0.8f);
  //     targetX  = Mathf.Clamp(targetX, doorWidth * -0.5f, doorWidth * 0.5f);
  //     float z  = Random.Range(-0.5f, 1.0f) + doorLocalZ + walkInDistance;
  //
  // Root-local, like everything else `area.transform.TransformPoint` touches.
  waitingAreaFlowTargetX: 0,
  // 🔍 `.ctor` 0x4C = 4.54 (`0x409147ae4060a3d7`, high half). Was 6.0, a guess.
  //
  // Nothing in the mesh pins this one: the back of the platform has no wall to
  // speak of — `BusStation.obj`'s back strip only has returns at the corners,
  // x < -9.5 and x > 9.5, so the whole 19-unit span is open. It is a crowd
  // knob, not a doorway, and its tooltip says so: "narrower = more squeeze".
  //
  // (The **front** wall does have a doorway, and the mesh shows it exactly:
  // vertices above y = 1 stop at x = ±3.5, matching `Door1`/`Door2` at 3.512
  // each. That is what `exitDoorWidth` sits inside — see below.)
  waitingAreaDoorWidth: 4.54,
  // 🔍📐 `.ctor` 0x48 = 3.51 (`0x4060a3d7`), and here the `.ctor` and the mesh
  // **agree**: `BusStation.obj`'s back extent is z = +1.059 in `BusStation`'s
  // space, so +3.699 root-local, and 3.51 sits 0.19 inside it.
  //
  // That agreement is the whole reason this one is adopted while the exit door
  // was not. `.ctor` holds a symmetric pair, exitDoorLocalZ = -3.51 and
  // doorLocalZ = +3.51, typed together; the exit half is provably wrong (the
  // real leaves are at -4.6355) so the pair is suspect — but the entry half
  // lands on the geometry anyway. Was 2.2, a guess.
  waitingAreaDoorLocalZ: 3.51,
  // 🔍 `.ctor` 0x50 = 1.64 (`0x3fd1eb85`). Was 0.4, a guess.
  //
  // Tooltip: "How far behind the door characters land before walking in", and
  // the sign works out: 3.51 + 1.64 = 5.15, so arrivals land at
  // z ∈ [4.65, 6.15] — **behind** `BusStationGround`'s back edge (+4.149) —
  // and the crowd flow walks them in. With the old 2.2 + 0.4 they landed at
  // z ≈ 2.6, already well inside the room, which the tooltip rules out.
  waitingAreaWalkInDistance: 1.64,
  //
  // Two `[SerializeField]`s of this group are **dead**: `walkSpeed` (0x54,
  // "Walking speed through the door (units/sec)") and `jumpPower` (0x9C) have
  // no read anywhere in the decomp — `Receive` uses `jumpDuration` instead.
  // `jumpPower`'s own tooltip admits it ("Unused for entry walk, kept for
  // backwards compatibility"); `walkSpeed`'s does not, but the code agrees.
  // The port has neither, which is correct.
  // 📐 RECOVERED from the prefab hierarchy: -4.6355.
  //
  // `BoardOnePerson` computes the walk-out target as
  // `area.transform.TransformPoint(Random.Range(±ExitDoorWidth/2), y,
  // ExitDoorLocalZ)` — `area.transform` is the `WaitingArea` **root**, so this
  // number lives in root-local space. The doors do not:
  //
  //     WaitingArea (root)
  //       BusStation            z = +2.6400
  //         Door1 / Door2       z = -7.2755   ->  root-local -4.6355
  //         BusStationGround    z ∈ [-7.293, 1.509]  ->  root-local [-4.653, 4.149]
  //       BoardingPoint         z = -7.2      (a DIRECT child: already root-local)
  //
  // -4.6355 lands on `BusStationGround`'s front edge (-4.653) to within 2 cm:
  // the doorway is cut into the wall at the lip of the floor, which is exactly
  // where the tooltip says people leave from.
  //
  // ⚠️ It was -7.2755 — the `Door1`/`Door2` node's z read **in `BusStation`'s
  // space** and used as if it were the root's, missing the +2.64 the parent
  // adds. The note that justified it ("level with `BoardingPoint` (-7.2)")
  // compared two different spaces. The effect: passengers walked the whole way
  // out to the kerb and then hopped *backwards* onto the bus — the walk-out
  // target sat 0.0755 **past** the bus's own stop line. Before that it was a
  // flat guess of -3.1.
  //
  // The `.ctor` field initialiser says -3.51, which matches neither the floor
  // nor the doorway; see the stale-block warning in checklist §4.
  waitingAreaExitDoorLocalZ: -4.6355,
  waitingAreaExitDoorWidth: 2.4,

  // ❓ PLACEHOLDER (magnitudes only — the *shape* of the animation is 🔍, see
  // `WaitingArea.updateDoors`).
  //
  // `WaitingArea$.ctor__0x2136444.c` initialises 0x30/0x34 from the 8-byte
  // store `0xbf0000003fc00000` — low half 0x3fc00000 = 1.5 into
  // `doorOpenSlideDistance`, high half 0xbf000000 = -0.5 into
  // `doorOpenSlideZ`. Those are the values used here.
  //
  // They are only the **C# field initialisers**, though, and Unity overwrites
  // every `[SerializeField]` with what the prefab serialised — which
  // AssetRipper could not export (`WaitingArea.prefab`'s MonoBehaviour block
  // has no fields at all). And the same ctor block is provably stale for its
  // neighbours: it says `areaBoundsSize = (14, 6.1)` where the floor mesh
  // measures 20.2 x 8.8, and `exitDoorLocalZ = -3.51` where the `Door1` /
  // `Door2` nodes actually sit at -7.2755. So treat 1.5 / -0.5 as a starting
  // point with a source, not as the shipped numbers.
  //
  // What was here before was worse: a flat `slide: 3.4` in `environmentView`
  // with no source at all, chosen so the leaves fully cleared the doorway.
  //
  // To settle: measure the gap between the leaves in `reference/gameplay.mp4`
  // while a bus is boarding, or recover the prefab's serialised MonoBehaviour.
  waitingAreaDoorOpenSlideDistance: 1.5,
  waitingAreaDoorOpenSlideZ: -0.5,

  // The kinematic crowd, all 🔍 from `WaitingArea$.ctor__0x2136444.c`. Every
  // one of these was a hand-tuned guess before; every one now comes from the
  // `.ctor` field-initialiser block.
  //
  // That block is stale for the room's **geometry** (checklist §4: it says the
  // plate is 14 x 6.1 where the mesh measures 20.2 x 8.8, and puts the exit
  // door a unit inside the real one). Its **simulation** half is a different
  // story: five of these the port had already arrived at independently, from
  // the HUD, and hit **exactly** — `smoothTime` 0.12, `tiltMultiplier` 4,
  // `maxWobbleDuration` 1.2, `flowTargetXZ.x` 0, `baseFacingAngle` 180. Five
  // exact agreements is not what a stale block looks like, so the rest of the
  // group is taken from it too.
  //
  // What separates the stale half of the block from the sound half turns out
  // to be the `[Header]` the fields sit under. Both proven-stale values are
  // geometry — `areaBoundsSize` under "Area Bounds (Local XZ)" and
  // `exitDoorLocalZ` under "Exit Door" — which is exactly what a designer
  // re-drags after laying the prefab out. Everything under "Kinematic Crowd
  // Flow", "Roly-Poly" and "Scatter Settings" is a simulation knob, and that
  // is where all five exact agreements are.
  //
  // `characterRadius` (0x58) is the one field of this group that the `.ctor`
  // gets **wrong**, and a frame of the original is what proves it.
  //
  // 📐 RECOVERED from `reference` frame `level 4, t=0` (the 230x498 capture in
  // this ticket), by autocorrelating luminance along two runs of people in the
  // **same image**, so no camera constant enters:
  //
  //     top-left queue, red straight run  (x 24..67, y 85..101):  period 7.0 px
  //     waiting-area crowd, blue band     (x 62..105, y 142..174): period 7.0 px
  //
  // The queue's period is a known length: `PeopleQueue.spacing` = 1.4 ("arc
  // distance between two rows", 🔍 `.ctor` 0x38, and the port already ships it).
  // The crowd's period is `characterRadius * 2 + skinWidth`. They measure the
  // **same 7.0 px**, so
  //
  //     2r + 0.02 = 1.4   ->   r = 0.69
  //
  // Two independent checks agree:
  //
  //  - Occupancy. 94 bodies at 1.4 pitch cover 94 * 1.4^2 * 0.866 = 159 u^2 of
  //    the 178 u^2 plate — 90 %, i.e. wall to wall with the ragged edges the
  //    frame shows. At 0.42 (pitch 0.86) they cover 60 u^2, a third of it, and
  //    `check:crowd` measures exactly that: three islands with 4.4 u and 4.8 u
  //    of bare floor between them where the original has three touching bands.
  //  - Band extents. The frame puts blue at x [-10.0, -1.9], red [-1.8, 2.9],
  //    yellow [3.0, 10.0] (contiguous). `npm run check:crowd 4 30` gives
  //    [-9.4, -2.3] / [-1.1, 1.5] / [2.7, 9.4] at r = 0.69 and
  //    [-9.7, -5.6] / [-1.2, 1.2] / [6.0, 9.7] at r = 0.42.
  //
  // ⚠️ The `.ctor`'s 0.42 decodes cleanly (0x58 = `0x3ed70a3d`) — it is simply
  // the C# **field initialiser**, which Unity overwrites with what the prefab
  // serialised, and AssetRipper exported this prefab's MonoBehaviour as an
  // empty stub. The same block is already known stale for `areaBoundsSize`
  // (14 x 6.1 against a 20.2 x 8.8 mesh) and `exitDoorLocalZ` (-3.51 against
  // -4.6355). The `[Header]` rule in checklist §4 said the stale half was only
  // the geometry and that "Kinematic Crowd Flow" could be trusted whole; that
  // rule is now **too coarse** — `characterRadius` sits under the crowd header
  // and is stale anyway. Fixed there.
  //
  // The port's own earlier 0.62 (`Char.obj` 2.71 wide x `Stickman`'s 0.4576 =
  // 1.24, "shoulders exactly touching") was the right instinct and the wrong
  // step: the collision diameter is the **queue's row pitch**, and the drawn
  // body is wider than that — see `queueCharacterScale`.
  // ❓ Still measured off a **still**, at about 5 px per world unit, so it is
  // good to a few percent and no better. To retire it: count heads on the
  // plate in `reference/gameplay.mp4` and measure their spacing against the
  // platform's known 20.2 width, over a frame where the room is full. The HUD
  // slider stays until then — see `tests/hudSliders.test.ts`.
  waitingAreaCharacterRadius: 0.69,
  waitingAreaSkinWidth: 0.02,
  waitingAreaFlowSpeed: 6,
  // 🔍 `flowTargetXZ` (0x64) is a `Vector2` and the `.ctor` zeroes **both**
  // halves: `*(undefined4 *)(param_1 + 100) = 0` and
  // `*(undefined4 *)(param_1 + 0x68) = 0`. So the crowd packs to the middle of
  // the room, not toward the road.
  //
  // Was -2.8, on the reasoning that "the crowd packs toward the road-facing
  // edge, which is -Z". Nothing in the decomp says that; the boarding walk is
  // what carries people to the door, and it starts from wherever they stand.
  waitingAreaFlowTargetZ: 0,
  // 🔍 `.ctor` 0x6C = 0x3df5c28f. The port already had 0.12 — an exact hit.
  waitingAreaSmoothTime: 0.12,
  // 🔍 `.ctor` 0xA8 = 3. `[Range(1, 10)]`, tooltip "higher = tighter packing,
  // no bounce".
  //
  // Was 8, picked by sweeping level 3: at 4 the worst settled same-colour pair
  // rested 0.74 apart against a 1.30 floor, at 8 it rested 1.04. That sweep
  // measured a real thing but answered the wrong question — it asked which
  // value looks tidiest, not which one shipped. With the radius now 0.42 the
  // floor it was chasing is 0.86, not 1.30, and 3 passes have far less work to
  // do.
  waitingAreaOverlapIterations: 3,
  // 🔍 The roly-poly group, `.ctor` 0x74..0x88. Two of the six the port had
  // already guessed exactly (`tiltMultiplier` 4, `maxWobbleDuration` 1.2);
  // the other four move, and all four move the same way — the original leans
  // **further** and settles **slower** than the port's guesses did.
  waitingAreaTiltMultiplier: 4,
  waitingAreaMaxTilt: 30,
  waitingAreaWobbleStiffness: 200,
  waitingAreaWobbleDamping: 5,
  waitingAreaStopWobbleBoost: 15,
  waitingAreaMaxWobbleDuration: 1.2,
  // RECOVERED from the tooltip: "Base Y rotation for characters. 180 means they
  // face Z- (towards camera)."
  waitingAreaBaseFacingAngle: 180,
  // 🔍 `.ctor` 0xA4 = 15 (was 20, a guess).
  waitingAreaRandomRotationOffset: 15,

  // `PeopleQueue.laneCount` = 3 — three abreast.
  //
  // ⚠️ This block used to carry a second claim: that across all 40 levels every
  // colour run is a multiple of 6, and that 6 "is the release batch,
  // `laneCount * maxRowsPerBatch` = 3 * 2". The second half is **wrong** and
  // contradicts the block on `maxRowsPerBatch` above, which reads the field as
  // the int 3 (batch = 3 * 3 = 9) and shows why run lengths cannot carry the
  // batch size at all: `ReleaseFromTrays` breaks out of a batch the moment the
  // front colour changes, so no batch ever straddles a run boundary. The
  // multiple-of-6 fact is real but it is an **authoring** property of the
  // levels, and it lives in `RUN_GRID` in `scripts/invariants.ts`.
  //
  // Bus capacities line up on the same grid: 18 / 36 / 54 = 3 / 6 / 9 batches,
  // or 6 / 12 / 18 rows of three.
  // 🔍 `PeopleQueue$$.ctor` 0x48 / 0x4C / 0x38 / 0x40 / 0x3C — see the block on
  // the interface above. `laneCount` and `spacing` the port had already reached
  // independently; the other three were guesses (1.3 / 6 / 6) and are now read.
  queueLaneCount: 3,
  queueLaneSpacing: 1.4,
  queueRowSpacing: 1.4,
  queueAdvanceSpeed: 6.72,
  queueSamplesPerSegment: 8,
  // 📐 1.13, pinned from **both sides**, and deliberately not the `.ctor`'s 1.55.
  //
  // Lower bound — the original frame. In the top-left queue, rows sit
  // `queueRowSpacing` = 1.4 apart (🔍 `.ctor` 0x38), so whether a drawn body is
  // narrower or wider than 1.4 is directly visible as belt showing through
  // between rows. Classifying pixels by hue along the red run:
  //
  //     original (y 84..104, every row):  43 px of solid red, no belt pixel
  //     port at scale 1 (y 100..136):     a 1-4 px belt gap at every height
  //
  // `Char.obj` is 2.7104 wide and `Stickman` draws it at 0.4576334, so a body
  // is 1.2402 across at scale 1. No gap therefore means
  // s >= 1.4 / 1.2402 = **1.129**.
  //
  // Upper bound — the bus roof. `Bus_3_single.obj` is 1.649 half-wide at cabin
  // height, and `Char.obj`'s widest band is 0.625 half-wide (at prefab scale,
  // measured in twelve slices). An outer-column rider reaches
  // `passengerSideOffset + 0.625 * s`, so staying inside the bus's own
  // silhouette needs 0.9 + 0.625 s <= 1.649, i.e. s <= **1.198**.
  //
  // The window is [1.129, 1.198] and 1.13 is its floor — the end with a direct
  // measurement rather than a bound behind it.
  //
  // ⚠️ **The `.ctor` says 1.55 and that is falsified by the port's own screen.**
  // 1.55 puts the outer column at 0.9 + 0.969 = 1.869 against a 1.649 half-roof,
  // and passengers visibly hang over the side of every bus; it also stands a
  // rider 2.8 + 3.61 = 6.41 high, so heads clear the 4.01-tall `ConveyorClose`
  // by a wide margin and show on the tunnel roof. Tried, looked at, reverted.
  // 0x44 belongs to the same C# field-initialiser block as
  // `WaitingArea.characterRadius` 0.42 — read cleanly, overwritten by the
  // prefab, and stale. See checklist §4.
  //
  // For the record, the old justification for 1 — "1.24 is near enough
  // `queueLaneSpacing`" — was inference dressed as a reading, and the frame
  // rules it out: at 1.24 the belt shows between rows and in the frame it does
  // not.
  // ❓ Both bounds come off a **still** at about 5 px per world unit, so 1.13
  // is good to a few percent and no better.
  //
  // 📐 **The clip has now been measured too, and it seconds this without
  // narrowing it.** That was the retirement plan written here — *"measure a
  // body against the platform's known 20.2 width in `reference/gameplay.mp4`"*
  // — and it was blocked by PS-10 until ffmpeg turned up. At t = 1.0 s the
  // camera draws **8.0 px per world unit** (11.2 px per 1.4-unit queue step,
  // cross-checked by the 172 px waiting-area rail over a 20.2-wide plate), and
  // a head measures **12.5 ± 1.5 px**, i.e. 1.56 ± 0.19 units, i.e. a scale of
  // roughly **[1.11, 1.41]**. Looser than the still's [1.129, 1.198], so the
  // number does not move — but the two measurements share no assumption and
  // **both exclude 1.55**. Frozen in `tests/characterSize.test.ts`.
  //
  // To actually tighten it, something better than a 332x720 clip is needed: a
  // head is eleven pixels across in it. The HUD slider stays until then — see
  // `tests/hudSliders.test.ts`.
  queueCharacterScale: 1.13,
}

/**
 * A tuning set nothing else shares.
 *
 * `{ ...DEFAULT_GAMEPLAY_TUNING }` is a shallow copy, so the HUD sliders would
 * write straight through the per-size extension records into the module-level
 * default and into every other session in the process. Copy those by hand.
 */
export function cloneGameplayTuning(
  source: GameplayTuning = DEFAULT_GAMEPLAY_TUNING,
): GameplayTuning {
  return {
    ...source,
    passengerHeadExtension: { ...source.passengerHeadExtension },
    passengerTailExtension: { ...source.passengerTailExtension },
    waitingAreaBounds: { ...source.waitingAreaBounds },
  }
}
